import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  ButtonBase,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  Chip,
  Paper,
  Popover,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FilterListIcon from "@mui/icons-material/FilterList";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import SummarizeIcon from "@mui/icons-material/Summarize";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import IosShareIcon from "@mui/icons-material/IosShare";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GridOnIcon from "@mui/icons-material/GridOn";
import AddIcon from "@mui/icons-material/Add";
import { generateAuditedClient } from "../audit/auditedClient";
import {
  getWBRCallouts,
  getCalloutSummary,
  getWbrCalloutStatus,
} from "../graphql/queries";
import { updateCalloutSummary } from "../graphql/mutations";
import { GetWBRCalloutsInput, WBRReportMeta, User } from "../models/API";
import type {
  Callout,
  AddCalloutInput,
  UpdateCalloutInput,
  CalloutSummary,
  KpiCell,
  KeyCalloutRef,
} from "../models/API";
import { useIsMobile } from "../hooks/useIsMobile";
import { useUnifiedCallouts } from "../hooks/useUnifiedCallouts";
import { parseUserAttributes } from "./utils/userAttributes";
import CalloutEditor from "./wbr/CalloutEditor";
import CalloutCard from "./wbr/CalloutCard";
import SummaryEditor from "./wbr/SummaryEditor";
import SummarySection from "./wbr/SummarySection";
import { canEditSummaryDecision } from "./wbr/canEditSummary";
import WbrPublishUploadButton from "./WbrPublishUploadButton";
import WBRChatbox, { WbrViewContext, WbrViewCallout } from "./wbr/WBRChatbox";

// Amazon-themed colors
const NAVY = "#232F3E";
const ACCENT_ORANGE = "#FF9900";

// Skeleton shimmer placeholder style — replaces CircularProgress for full-view loading states
const skeletonSx = {
  background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite",
  borderRadius: 2,
};

// Self-contained base stylesheet for the structured summary section. The viewer
// historically relied on `calloutStyles` scraped from the report's
// `_callouts.html` for these classes, but the structured store renders from the
// DB with no `_callouts.html` dependency — so when that scrape is absent the
// section was unstyled. This block ports the canonical WBR document CSS (see
// SPBFinnLambda `wbr_html_template.py`) for the summary markup so SummarySection
// always renders styled. No `!important` here, so a scraped `calloutStyles` and
// the `EXEC_SUMMARY_PANEL_OVERRIDES` (which use `!important`) still take precedence.
const SUMMARY_BASE_STYLES = `<style>
  .exec-summary { background: #FFFFFF; border-radius: 12px; box-shadow: 0 2px 10px rgba(0,0,0,0.10); padding: 28px 32px; border-top: 4px solid #1B3A6B; }
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #E0E7FF; }
  .doc-title { font-size: 22px; font-weight: bold; color: #1B3A6B; letter-spacing: -0.3px; }
  .doc-subtitle { font-size: 13px; color: #9E9E9E; margin-top: 4px; }
  .doc-meta { text-align: right; }
  .doc-meta-week { font-size: 20px; font-weight: bold; color: #1B3A6B; }
  .doc-meta-label { font-size: 11px; color: #9E9E9E; text-transform: uppercase; letter-spacing: 0.5px; }

  .kpi-strip { display: flex; gap: 0; margin-bottom: 24px; border: 1px solid #E8ECF4; border-radius: 8px; overflow: hidden; }
  .kpi-cell { flex: 1; padding: 14px 16px; border-right: 1px solid #E8ECF4; background: #FAFBFF; }
  .kpi-cell:last-child { border-right: none; }
  .kpi-cell.highlight { background: #E3F0FF; }
  .kpi-label { font-size: 10px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.6px; color: #757575; margin-bottom: 4px; }
  .kpi-cell.highlight .kpi-label { color: #1B3A6B; }
  .kpi-value { font-size: 20px; font-weight: bold; color: #000000; }
  .kpi-cell.highlight .kpi-value { color: #1B3A6B; }
  .kpi-value.green { color: #0a7c5c; }
  .kpi-value.red, .kpi-value.neg { color: #D32F2F; }
  .kpi-value.orange { color: #d97706; }
  .kpi-value.blue { color: #2563eb; }
  .kpi-delta { font-size: 12px; font-weight: 600; margin-top: 2px; }
  .kpi-cell.highlight .kpi-delta { color: #424242; }

  .exec-section-header { font-size: 11px; font-weight: bold; text-transform: uppercase; color: #1B3A6B; letter-spacing: 0.8px; margin: 22px 0 10px 0; padding-bottom: 5px; border-bottom: 1px solid #E0E7FF; }
  .narrative { font-size: 14px; color: #2C2C2C; line-height: 1.75; margin-bottom: 10px; }

  .callout-table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  .callout-table thead tr { background: #1B3A6B; }
  .callout-table thead th { font-size: 11px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; color: #FFFFFF; padding: 9px 12px; text-align: left; }
  .callout-table tbody tr { border-bottom: 1px solid #F0F0F0; }
  .callout-table tbody tr:last-child { border-bottom: none; }
  .callout-table tbody tr:nth-child(even) { background: #FAFBFF; }
  .callout-table tbody td { font-size: 13px; color: #2C2C2C; padding: 10px 12px; vertical-align: top; line-height: 1.5; }
  .callout-num { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; color: #FFFFFF; font-weight: bold; font-size: 12px; flex-shrink: 0; }
  .callout-ref { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
  .callout-ref-page { font-size: 12px; color: #757575; font-weight: bold; text-transform: uppercase; }
  .callout-num.pos-num { background: #2E7D32; }
  .callout-num.neg-num { background: #B85450; }
  .callout-title-text { font-weight: bold; color: #1B3A6B; font-size: 13px; }
  .callout-title-sub { font-size: 12px; color: #757575; font-weight: normal; margin-top: 2px; }

  .summary-banner { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: #E8ECF4; border-radius: 10px; overflow: hidden; margin: 0 0 24px 0; border: 1px solid #E8ECF4; }
  .summary-cell { background: #FFFFFF; padding: 16px 18px; }
  .summary-cell-label { font-size: 10px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.8px; color: #9CA3AF; margin-bottom: 6px; }
  .summary-cell-value { font-size: 26px; font-weight: 800; letter-spacing: -1px; line-height: 1; margin-bottom: 4px; }
  .summary-cell-value.green  { color: #0a7c5c; }
  .summary-cell-value.ink    { color: #111827; }
  .summary-cell-value.orange { color: #d97706; }
  .summary-cell-value.blue   { color: #2563eb; }
  .summary-cell-sub { font-size: 11px; color: #9CA3AF; margin-top: 2px; }

  .pos { color: #2E7D32; }
  .neg { color: #D32F2F; }
  .neutral { color: #757575; }
</style>`;

// CSS overrides for exec summary when rendered inside the split-pane viewer
const EXEC_SUMMARY_PANEL_OVERRIDES = `<style>
  /* Remove fixed max-widths — fill the pane */
  .summary-banner { max-width: 100% !important; margin: 0 0 16px 0 !important; }
  .exec-summary { max-width: 100% !important; margin: 0 0 16px 0 !important;
    padding: 20px 20px !important; border-radius: 8px !important;
    border: none !important; box-shadow: none !important; border-top: none !important; }

  /* KPI strip — responsive wrap, no border */
  .kpi-strip { flex-wrap: wrap !important; border: none !important; }
  .kpi-cell { flex: 1 1 140px !important; min-width: 120px !important;
    border-right: none !important; }

  /* Summary banner — no outer border */
  .summary-banner { max-width: 100% !important; margin: 0 0 16px 0 !important;
    border: none !important; }

  /* Callout table — scrollable */
  .callout-table { display: block !important; overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important; }

  /* Hide the footer */
  .exec-footer { display: none !important; }

  /* Tighter section header spacing */
  .exec-section-header { margin: 16px 0 8px 0 !important; }

  /* Narrative paragraphs — slightly tighter */
  .narrative { font-size: 13px !important; line-height: 1.65 !important;
    margin-bottom: 8px !important; }

  /* Summary banner cells — compact */
  .summary-cell { padding: 12px 14px !important; }
  .summary-cell-value { font-size: 22px !important; }

  /* ── 1. Staggered fade-in on summary banner cells ── */
  @keyframes cellFadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .summary-cell {
    animation: cellFadeIn 0.4s ease both !important;
  }
  .summary-cell:nth-child(1) { animation-delay: 0s !important; }
  .summary-cell:nth-child(2) { animation-delay: 0.08s !important; }
  .summary-cell:nth-child(3) { animation-delay: 0.16s !important; }
  .summary-cell:nth-child(4) { animation-delay: 0.24s !important; }

  /* ── 2. Value pop animation on load ── */
  @keyframes valuePop {
    0%   { transform: scale(0.7); opacity: 0; }
    60%  { transform: scale(1.08); }
    100% { transform: scale(1); opacity: 1; }
  }
  .summary-cell-value {
    animation: valuePop 0.5s ease both !important;
  }
  .summary-cell:nth-child(1) .summary-cell-value { animation-delay: 0.1s !important; }
  .summary-cell:nth-child(2) .summary-cell-value { animation-delay: 0.18s !important; }
  .summary-cell:nth-child(3) .summary-cell-value { animation-delay: 0.26s !important; }
  .summary-cell:nth-child(4) .summary-cell-value { animation-delay: 0.34s !important; }

  /* ── 3. KPI cell hover highlight ── */
  .kpi-cell {
    transition: background 0.15s ease, transform 0.15s ease !important;
  }
  .kpi-cell:hover {
    background: #EEF2FF !important;
    transform: translateY(-1px) !important;
  }
  .kpi-cell.highlight:hover {
    background: #D6E8FF !important;
  }

  /* ── 4. Callout table row hover + color-coded left border ── */
  .callout-table tbody tr {
    transition: background 0.12s ease !important;
    border-left: 3px solid transparent !important;
  }
  .callout-table tbody tr:hover {
    background: #F5F7FF !important;
  }
  .callout-table tbody tr:has(.callout-num.pos-num) {
    border-left: 3px solid #2E7D32 !important;
  }
  .callout-table tbody tr:has(.callout-num.neg-num) {
    border-left: 3px solid #B85450 !important;
  }

  /* ── 5. Narrative left accent bar ── */
  .narrative {
    border-left: 3px solid #E0E7FF !important;
    padding-left: 12px !important;
    transition: border-color 0.2s ease !important;
  }
  .narrative:hover {
    border-left-color: #3B82F6 !important;
  }

  /* ── 6. Section header colored underline accent ── */
  .exec-section-header {
    border-bottom: 2px solid #3B82F6 !important;
    padding-bottom: 6px !important;
  }

  /* ── 7. Summary banner colored top borders ── */
  .summary-cell:nth-child(1) { border-top: 3px solid #d97706 !important; }
  .summary-cell:nth-child(2) { border-top: 3px solid #0a7c5c !important; }
  .summary-cell:nth-child(3) { border-top: 3px solid #111827 !important; }
  .summary-cell:nth-child(4) { border-top: 3px solid #2563eb !important; }

  /* ── Exec summary fade-in ── */
  @keyframes execFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .exec-summary {
    animation: execFadeIn 0.4s ease both !important;
    animation-delay: 0.15s !important;
  }
</style>`;

// Mobile-specific overrides for the Executive Summary tab — converts the
// key-callouts table into stacked cards so it's readable on narrow screens.
const MOBILE_EXEC_SUMMARY_OVERRIDES = `<style>
  /* Summary banner — 2x2 grid on mobile */
  .summary-banner {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 1px !important;
    border-radius: 12px !important;
    overflow: hidden !important;
    background: #E8ECF4 !important;
    margin-bottom: 16px !important;
  }
  .summary-cell {
    padding: 16px 12px !important;
    background: #fff !important;
    text-align: center !important;
  }
  .summary-cell-label {
    font-size: 10px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    color: #6B7280 !important;
    margin-bottom: 4px !important;
  }
  .summary-cell-value {
    font-size: 24px !important;
    font-weight: 700 !important;
  }

  /* KPI strip — horizontal scroll on mobile */
  .kpi-strip {
    display: flex !important;
    flex-direction: row !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    gap: 8px !important;
    padding: 8px 0 !important;
    margin-bottom: 16px !important;
  }
  .kpi-cell {
    flex: 0 0 auto !important;
    min-width: 140px !important;
    border: 1px solid #E8ECF4 !important;
    border-radius: 10px !important;
    padding: 12px !important;
    background: #fff !important;
  }
  .kpi-cell.highlight {
    background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%) !important;
    border-color: #93A3D8 !important;
  }

  /* ── Key Callouts table → fancy stacked cards ── */
  .callout-table { display: block !important; overflow: visible !important; }
  .callout-table thead { display: none !important; }
  .callout-table tbody {
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
  }
  .callout-table tbody tr {
    display: block !important;
    background: #fff !important;
    border-radius: 12px !important;
    padding: 0 !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
    overflow: hidden !important;
    border: none !important;
  }
  .callout-table tbody tr:nth-child(even) { background: #fff !important; }

  /* Color-coded top border based on callout type */
  .callout-table tbody tr:has(.callout-num.pos-num) {
    border-top: 4px solid #2E7D32 !important;
  }
  .callout-table tbody tr:has(.callout-num.neg-num) {
    border-top: 4px solid #B85450 !important;
  }
  .callout-table tbody tr:not(:has(.callout-num.pos-num)):not(:has(.callout-num.neg-num)) {
    border-top: 4px solid #1B3A6B !important;
  }

  .callout-table tbody td {
    display: block !important;
    padding: 0 !important;
    border: none !important;
  }

  /* Row number cell — styled badge header */
  .callout-table tbody td:first-child {
    background: linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%) !important;
    padding: 10px 14px !important;
    border-bottom: 1px solid #E8ECF4 !important;
  }
  .callout-table tbody td:first-child .callout-num {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 28px !important;
    height: 28px !important;
    border-radius: 50% !important;
    font-size: 13px !important;
    font-weight: 700 !important;
  }
  .callout-table tbody td:first-child .callout-num.pos-num {
    background: #DCFCE7 !important;
    color: #166534 !important;
  }
  .callout-table tbody td:first-child .callout-num.neg-num {
    background: #FEE2E2 !important;
    color: #991B1B !important;
  }

  /* Callout title cell */
  .callout-table tbody td:nth-child(2) {
    padding: 12px 14px 8px 14px !important;
  }
  .callout-table tbody td:nth-child(2) .callout-title-text {
    font-size: 15px !important;
    font-weight: 600 !important;
    line-height: 1.4 !important;
    color: #1F2937 !important;
  }
  .callout-table tbody td:nth-child(2) .callout-type-badge {
    display: inline-block !important;
    margin-top: 6px !important;
    padding: 3px 8px !important;
    border-radius: 4px !important;
    font-size: 10px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.3px !important;
    background: #F3F4F6 !important;
    color: #6B7280 !important;
  }

  /* Summary & Key Metrics cell — the main content */
  .callout-table tbody td:nth-child(3) {
    padding: 12px 14px 14px 14px !important;
    font-size: 13px !important;
    line-height: 1.7 !important;
    color: #374151 !important;
    background: #FAFBFC !important;
  }
  /* Style positive/negative values in metrics */
  .callout-table tbody td:nth-child(3) .pos-val,
  .callout-table tbody td:nth-child(3) [style*="color: #2E7D32"],
  .callout-table tbody td:nth-child(3) [style*="color:#2E7D32"] {
    font-weight: 600 !important;
  }
  .callout-table tbody td:nth-child(3) .neg-val,
  .callout-table tbody td:nth-child(3) [style*="color: #B85450"],
  .callout-table tbody td:nth-child(3) [style*="color:#B85450"] {
    font-weight: 600 !important;
  }

  /* Narrative text — card style */
  .narrative {
    font-size: 13px !important;
    line-height: 1.65 !important;
    padding: 12px 14px !important;
    margin: 12px 0 !important;
    background: #fff !important;
    border-radius: 10px !important;
    border-left: 3px solid #3B82F6 !important;
    box-shadow: 0 1px 4px rgba(0,0,0,0.05) !important;
  }

  /* Section headers — more prominent */
  .exec-section-header {
    font-size: 13px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    color: #1B3A6B !important;
    margin: 20px 0 10px 0 !important;
    padding-bottom: 8px !important;
    border-bottom: 2px solid #3B82F6 !important;
  }

  /* Executive summary container */
  .exec-summary {
    padding: 12px !important;
    background: transparent !important;
  }
</style>`;

// CSS overrides for folder-level executive summary (full-width view)
const FOLDER_SUMMARY_OVERRIDES = `<style>
  .summary-banner { max-width: 100% !important; margin: 0 0 16px 0 !important; }
  .exec-summary { max-width: 100% !important; margin: 0 0 16px 0 !important;
    padding: 24px !important; border-radius: 8px !important;
    border: none !important; box-shadow: none !important; }
  .kpi-strip { flex-wrap: wrap !important; border: none !important; }
  .kpi-cell { flex: 1 1 180px !important; min-width: 150px !important;
    border-right: none !important; }
  .exec-footer { display: none !important; }
  .narrative { font-size: 14px !important; line-height: 1.7 !important; }
  .summary-cell { padding: 14px 16px !important; }
  .summary-cell-value { font-size: 24px !important; }
  .callout-table { display: block !important; overflow-x: auto !important; }
</style>`;

// Mobile-specific overrides for folder-level executive summary
const MOBILE_FOLDER_SUMMARY_OVERRIDES = `<style>
  @media (max-width: 768px) {
    .summary-banner {
      display: grid !important; grid-template-columns: 1fr 1fr !important;
      gap: 1px !important; border-radius: 12px !important; overflow: hidden !important;
    }
    .summary-cell { padding: 12px 10px !important; text-align: center !important; }
    .summary-cell-value { font-size: 20px !important; }
    .kpi-strip { flex-direction: column !important; }
    .kpi-cell { min-width: 100% !important; }
    .narrative { font-size: 13px !important; }
  }
</style>`;

// ---------------------------------------------------------------------------
// Week utilities — same ISO week logic as YhhsiungGunicornAuthWebapp
// ---------------------------------------------------------------------------

interface WeekInfo {
  year: number;
  week: number;
  startDate: Date; // Sunday
  endDate: Date; // Saturday
}

/**
 * Get ISO week number for a date. Uses the same algorithm as the VESL webapp:
 * weeks run Sunday–Saturday, and the week number is the ISO 8601 week of
 * (date + 1 day) so that Saturday maps to the same week as the preceding Sunday.
 */
function getISOWeekInfo(d: Date): WeekInfo {
  // Shift by +1 day so Saturday stays in the same week as Sunday
  const shifted = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  const iso = getISOWeekNumber(shifted);
  // Compute the Monday of the ISO week, then go back 1 day to get Sunday
  const monday = getISOWeekMonday(iso.year, iso.week);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() - 1);
  const saturday = new Date(monday);
  saturday.setDate(monday.getDate() + 5);
  return {
    year: iso.year,
    week: iso.week,
    startDate: sunday,
    endDate: saturday,
  };
}

function getISOWeekNumber(d: Date): { year: number; week: number } {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(
    ((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
  );
  return { year: tmp.getUTCFullYear(), week: weekNo };
}

function getISOWeekMonday(year: number, week: number): Date {
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7; // Mon=1 … Sun=7
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
  return monday;
}

/** Get the current WBR week (last week relative to today in PST). */
function getCurrentWBRWeek(): WeekInfo {
  // Approximate PST by subtracting 8 hours from UTC
  const now = new Date();
  const pst = new Date(now.getTime() - 8 * 60 * 60 * 1000);
  const lastWeek = new Date(pst);
  lastWeek.setDate(pst.getDate() - 7);
  return getISOWeekInfo(lastWeek);
}

/** Get total ISO weeks in a year. */
function getWeeksInYear(year: number): number {
  const dec31 = new Date(year, 11, 31);
  const info = getISOWeekNumber(
    new Date(dec31.getFullYear(), dec31.getMonth(), dec31.getDate() + 1)
  );
  return info.week === 1
    ? getISOWeekNumber(new Date(year, 11, 24 + 1)).week
    : info.week;
}

/** Format a date as "MMM D" (e.g. "Mar 15"). */
function fmtShort(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Format a date as "YYYY-MM-DD". */
function fmtISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Convert year + week to the "YYYY-WNN" string the Lambda expects. */
function weekToParam(year: number, week: number): string {
  return `${year}-W${String(week).padStart(2, "0")}`;
}

/** Format the WBR week-END date (Saturday) as YYYY-MM-DD — the date the
 *  wbr-publish / wbr-callout pipeline uses for the {week} folder segment. */
function formatWeekEndDate(saturday: Date): string {
  const y = saturday.getFullYear();
  const m = String(saturday.getMonth() + 1).padStart(2, "0");
  const d = String(saturday.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Parse a "YYYY-WNN" string from the URL into a WeekInfo, or return null. */
function parseWeekParam(param: string | null): WeekInfo | null {
  if (!param) return null;
  const m = param.match(/^(\d{4})-W(\d{1,2})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  if (week < 1 || week > 53) return null;
  const monday = getISOWeekMonday(year, week);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() - 1);
  const saturday = new Date(monday);
  saturday.setDate(monday.getDate() + 5);
  return { year, week, startDate: sunday, endDate: saturday };
}

/** Read the initial week from the URL ?week= param, falling back to current WBR week. */
function getInitialWeek(): WeekInfo {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = parseWeekParam(params.get("week"));
  return fromUrl ?? getCurrentWBRWeek();
}

// ---------------------------------------------------------------------------
// Shareable-link URL param helpers
// ---------------------------------------------------------------------------

/** All URL params owned by the WBR Callouts viewer. */
type ShareUrlState = {
  week?: string; // "YYYY-WNN"
  report?: string; // filename, or null for summary/merged views
  merged?: boolean; // true when in merged view
  page?: number; // 1-based PDF page
  tab?: "exec" | "callouts"; // right-pane tab
  cellRefs?: boolean; // cell-refs highlight toggle
  impact?: string; // "all" | "large" | "medium" | "small"
  team?: string; // current team name (for cross-user deep-links)
};

/** Parse an integer URL param within [min, max], returning undefined on invalid input. */
function parseIntParam(
  raw: string | null,
  min: number,
  max: number
): number | undefined {
  if (!raw) return undefined;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < min || n > max) return undefined;
  return n;
}

/**
 * Write the given state to the URL (non-destructive to unrelated params).
 * Removes keys whose value is null/undefined/default so links stay compact.
 * Uses history.replaceState so there's no back-button noise.
 */
function writeShareUrl(state: ShareUrlState) {
  const p = new URLSearchParams(window.location.search);
  const setOrDelete = (key: string, value: string | undefined | null) => {
    if (value === null || value === undefined || value === "") {
      p.delete(key);
    } else {
      p.set(key, value);
    }
  };
  setOrDelete("week", state.week);
  setOrDelete("report", state.report);
  setOrDelete("merged", state.merged ? "true" : undefined);
  setOrDelete(
    "page",
    state.page && state.page > 1 ? String(state.page) : undefined
  );
  setOrDelete("tab", state.tab === "callouts" ? "callouts" : undefined);
  // Only serialize cellRefs when it differs from the default (true)
  setOrDelete("cellRefs", state.cellRefs === false ? "false" : undefined);
  setOrDelete(
    "impact",
    state.impact && state.impact !== "all" ? state.impact : undefined
  );
  // Team name — enables shareable links to land on the right team for viewers
  // who have access to multiple teams. MainPage reads ?team= on initial load.
  setOrDelete("team", state.team);
  // Pin the menu-level view so recipients with a different default menu item
  // still land on the WBR Callouts viewer. MainPage reads ?view= on mount.
  setOrDelete("view", "wbr-callouts");
  const qs = p.toString();
  const newUrl = qs
    ? `${window.location.pathname}?${qs}`
    : window.location.pathname;
  window.history.replaceState({}, "", newUrl);
}

// pdf.js CDN — using unpkg which hosts the UMD build
const PDFJS_CDN = "https://unpkg.com/pdfjs-dist@3.11.174/build";

/** Represents one parsed page of callout cards */
interface CalloutPage {
  pageNumber: number;
  title: string;
  html: string;
}

/** Represents a single page section in the merged callout view */
type MergedPageSection = {
  page_number: number;
  source_report: string;
  source_filename: string;
  title?: string;
  callouts: Callout[];
};

/** Represents a single entry in the page offset map */
type PageOffsetEntry = {
  showing_name: string;
  start_page: number;
  page_count: number;
};

/** Maps source PDF filenames to their page offset info in the merged PDF */
type PageOffsetMap = Record<string, PageOffsetEntry>;

interface WBRCalloutsViewerProps {
  isOpen: boolean;
  menuWidth: number;
  currentTeamId: number;
  currentTeamName: string;
  currentUserName: string;
  userConfig: User | null;
  effectiveRole?: string;
}

/**
 * Parse the callout HTML string into per-page sections and executive summary.
 */
function parseCalloutPages(html: string): {
  pages: CalloutPage[];
  styles: string;
  execSummaryHtml: string;
} {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const styleEls = doc.querySelectorAll("style");
  let styles = "";
  styleEls.forEach((el) => {
    styles += el.outerHTML;
  });

  // Extract executive summary + summary banner
  let execSummaryHtml = "";
  const summaryBannerEl = doc.querySelector(".summary-banner");
  if (summaryBannerEl) {
    // Summary banner goes first (moved to top)
    execSummaryHtml += (summaryBannerEl as HTMLElement).outerHTML;
  }
  const execSummaryEl = doc.querySelector(".exec-summary");
  if (execSummaryEl) {
    // Remove doc-header and doc-subtitle (everything above metrics)
    const header = execSummaryEl.querySelector(".doc-header");
    if (header) header.remove();
    const subtitle = execSummaryEl.querySelector(".doc-subtitle");
    if (subtitle) subtitle.remove();

    // Remove the Status column (last column) from the callout table
    const table = execSummaryEl.querySelector(".callout-table");
    if (table) {
      // Remove last <th> from thead
      table.querySelectorAll("thead tr").forEach((row) => {
        const cells = row.querySelectorAll("th");
        if (cells.length > 0) cells[cells.length - 1].remove();
      });
      // Remove last <td> from each tbody row
      table.querySelectorAll("tbody tr").forEach((row) => {
        const cells = row.querySelectorAll("td");
        if (cells.length > 0) cells[cells.length - 1].remove();
      });
    }

    execSummaryHtml += (execSummaryEl as HTMLElement).outerHTML;
  }

  const titles = doc.querySelectorAll(".page-section-title");
  const pages: CalloutPage[] = [];

  titles.forEach((titleEl) => {
    const titleText = titleEl.textContent?.trim() ?? "";
    const match = titleText.match(/Page\s+(\d+)/i);
    const pageNumber = match ? parseInt(match[1], 10) : pages.length + 1;

    let sectionHtml = (titleEl as HTMLElement).outerHTML;
    let sibling = titleEl.nextElementSibling;
    while (sibling) {
      if (sibling.classList.contains("page-section-title")) break;
      if (sibling.tagName === "HR" && sibling.classList.contains("divider")) {
        sibling = sibling.nextElementSibling;
        continue;
      }
      sectionHtml += (sibling as HTMLElement).outerHTML;
      sibling = sibling.nextElementSibling;
    }

    pages.push({ pageNumber, title: titleText, html: sectionHtml });
  });

  return { pages, styles, execSummaryHtml };
}

/**
 * Sort comparator for callouts by their visible card number (ascending).
 * Callouts without an assigned number (card_number <= 0 — e.g. freshly added
 * manual callouts) sink to the bottom. Ties break on DB id for stability.
 */
function compareByCardNumber(a: Callout, b: Callout): number {
  const an =
    a.card_number && a.card_number > 0
      ? a.card_number
      : Number.MAX_SAFE_INTEGER;
  const bn =
    b.card_number && b.card_number > 0
      ? b.card_number
      : Number.MAX_SAFE_INTEGER;
  if (an !== bn) return an - bn;
  return a.id - b.id;
}

/** Load pdf.js from CDN if not already loaded */
function loadPdfJs(): Promise<any> {
  const w = window as any;
  if (w.pdfjsLib) return Promise.resolve(w.pdfjsLib);

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${PDFJS_CDN}/pdf.min.js`;
    script.onload = () => {
      const lib = w.pdfjsLib;
      if (lib) {
        lib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.js`;
        resolve(lib);
      } else {
        reject(new Error("pdf.js failed to initialize"));
      }
    };
    script.onerror = () => reject(new Error("Failed to load pdf.js from CDN"));
    document.head.appendChild(script);
  });
}

const WBRCalloutsViewer: React.FC<WBRCalloutsViewerProps> = ({
  isOpen,
  menuWidth,
  currentTeamId,
  currentTeamName,
  currentUserName,
  userConfig,
  effectiveRole: effectiveRoleProp,
}) => {
  const isMobile = useIsMobile();
  const [reportList, setReportList] = useState<WBRReportMeta[]>([]);
  const [selectedReport, setSelectedReport] = useState<WBRReportMeta | null>(
    null
  );
  const [sourcePdfUrl, setSourcePdfUrl] = useState<string | null>(null);
  const [calloutHtmlUrl, setCalloutHtmlUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  // True while we are polling the callout bucket for a report whose callouts
  // are still being generated (drives the 'Generating callouts…' panel state).
  const [calloutGenerating, setCalloutGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfZoom, setPdfZoom] = useState<number>(100);
  // True while the canvases are being re-rendered after a zoom change. The
  // zoom effect hides the scroll container during the reflow, so without an
  // indicator the PDF appears to blank out / hang. Drives an overlay spinner.
  const [pdfZooming, setPdfZooming] = useState<boolean>(false);
  const [splitPercent, setSplitPercent] = useState<number>(75); // left pane width %
  const [reportMenuAnchor, setReportMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  // Week selector state — initialise from ?week= URL param if present
  const [selectedWeek, setSelectedWeek] = useState<WeekInfo>(getInitialWeek);
  const [weekPickerAnchor, setWeekPickerAnchor] = useState<null | HTMLElement>(
    null
  );
  const [pickerYear, setPickerYear] = useState<number>(
    () => getInitialWeek().year
  );

  // Pending report from ?report= URL param — auto-select once report list loads
  const [pendingReport, setPendingReport] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("report");
  });

  // Pending merged view from ?merged=true URL param — auto-enter once report list loads
  const [pendingMerged, setPendingMerged] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("merged") === "true";
  });

  // Pending PDF page from ?page= URL param — applied once the PDF is loaded
  const [pendingPage, setPendingPage] = useState<number | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const n = parseIntParam(params.get("page"), 1, 9999);
    return n ?? null;
  });

  // Parsed callout pages
  const [calloutPages, setCalloutPages] = useState<CalloutPage[]>([]);
  const [calloutStyles, setCalloutStyles] = useState<string>("");
  const [execSummaryHtml, setExecSummaryHtml] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPdfPages, setTotalPdfPages] = useState<number>(0);
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [rightTab, setRightTab] = useState<number>(() => {
    // 0 = Executive Summary, 1 = Callouts — seed from ?tab= URL param
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") === "callouts" ? 1 : 0;
  }); // 0 = Executive Summary, 1 = Callouts

  // ── edit-callout-summary: editable Narrative state ──────────────────────
  // The Narrative (Callout_Summary_Content) is read from the Callout_Summary_Store
  // via getCalloutSummary — NOT parsed from _callouts.html (Req 11.1). The banner
  // and key table are derived from the unified callouts (deriveBanner/deriveKeyCallouts).
  const [summaryContent, setSummaryContent] = useState<string | null>(null);
  const [summaryOverview, setSummaryOverview] = useState<string | null>(null);
  const [summaryKpiStrip, setSummaryKpiStrip] = useState<KpiCell[]>([]);
  const [summaryKeyCallouts, setSummaryKeyCallouts] = useState<KeyCalloutRef[]>(
    []
  );
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryEditorOpen, setSummaryEditorOpen] = useState<boolean>(false);
  // Tracked for last-write-wins bookkeeping; only the setter is consumed.
  const [, setSummaryUpdatedAt] = useState<string | null>(null);
  // The team_id that owns the currently loaded Narrative (for the permission gate).
  const [summaryTeamId, setSummaryTeamId] = useState<number | null>(null);
  // Saved pre-edit content used to roll back an optimistic render on failure.
  const [prevSummaryContent, setPrevSummaryContent] = useState<string | null>(
    null
  );
  // True while a save mutation is in flight (drives the editor's spinner).
  const [summarySaving, setSummarySaving] = useState<boolean>(false);

  // Folder-level executive summary state
  const [folderSummaryUrl, setFolderSummaryUrl] = useState<string | null>(null);
  const [folderSummaryHtml, setFolderSummaryHtml] = useState<string | null>(
    null
  );
  const [folderSummaryLoading, setFolderSummaryLoading] =
    useState<boolean>(false);
  const [folderSummaryError, setFolderSummaryError] = useState<string | null>(
    null
  );

  // Merged view state
  const [isMergedView, setIsMergedView] = useState<boolean>(false);
  const [mergedPdfUrl, setMergedPdfUrl] = useState<string | null>(null);
  const [mergedHighlightedPdfUrl, setMergedHighlightedPdfUrl] = useState<
    string | null
  >(null);
  const [highlightMap, setHighlightMap] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [mergedLoading, setMergedLoading] = useState<boolean>(false);
  const [mergedError, setMergedError] = useState<string | null>(null);
  const [pageOffsetMap, setPageOffsetMap] = useState<PageOffsetMap | null>(
    null
  );
  const [skippedFiles, setSkippedFiles] = useState<string[] | null>(null);

  // Continuous-scroll: ordered list of reports whose PDFs are appended into the
  // single scroll container, plus the active report (one centered in viewport).
  // The list is ordered earliest-first; loadNextReport pushes to the tail and
  // loadPrevReport unshifts to the head.
  const [appendedReports, setAppendedReports] = useState<WBRReportMeta[]>([]);
  const [appendingNext, setAppendingNext] = useState<boolean>(false);
  const [appendingPrev, setAppendingPrev] = useState<boolean>(false);
  // Synchronous in-flight guards. React state is async — within a single
  // scroll burst the closure-captured `appendingNext`/`appendingPrev` stays
  // false across rapid invocations of the scroll callback, so multiple
  // concurrent loads start and the divider+pages get appended twice. Refs
  // give us a same-tick guard that flips before the first await.
  const appendingNextRef = useRef<boolean>(false);
  const appendingPrevRef = useRef<boolean>(false);
  const calloutByReportRef = useRef<
    Map<
      string,
      {
        pages: CalloutPage[];
        styles: string;
        execSummaryHtml: string;
        calloutHtmlUrl: string | null;
      }
    >
  >(new Map());
  const pdfDocByReportRef = useRef<Map<string, any>>(new Map());
  // Per-report "has highlighted PDF" flag, captured at fetch time so the
  // cell-refs toggle button visibility tracks the active report (not just
  // the head of the continuous-scroll queue).
  const highlightedByReportRef = useRef<Map<string, boolean>>(new Map());
  // Per-report PDF URL bundle, captured at fetch time. Used by toggleCellRefs
  // to promote the currently-active appended report into "selected" state so
  // the post-toggle reload uses that report's URLs instead of head's.
  const pdfUrlsByReportRef = useRef<
    Map<
      string,
      {
        highlighted: string | null;
        original: string | null;
        isHighlighted: boolean;
      }
    >
  >(new Map());
  // Async fetch token to invalidate in-flight appends when context changes
  const appendTokenRef = useRef<number>(0);
  // Most-recent active report key, kept in a ref to avoid stale-closure reads
  // inside the scroll handler. lastActiveReportRef prevents redundant updates.
  const activeReportKeyRef = useRef<string | null>(null);
  const lastActiveReportRef = useRef<string | null>(null);
  // Callbacks the renderPdfPages scroll handler can invoke. Updated via refs
  // so the handler always sees the latest values without re-binding.
  const onActiveReportChangeRef = useRef<((key: string) => void) | null>(null);
  const onNearBottomRef = useRef<(() => void) | null>(null);
  const onNearTopRef = useRef<(() => void) | null>(null);

  // Refs
  const pdfScrollRef = useRef<HTMLDivElement>(null);
  const pdfCanvasContainerRef = useRef<HTMLDivElement | null>(null);
  const calloutPaneRef = useRef<HTMLDivElement>(null);
  const pageCanvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const pdfDocRef = useRef<any>(null);
  // Cell references toggle state — seed from ?cellRefs= URL param (default true)
  const [cellRefsEnabled, setCellRefsEnabled] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("cellRefs") !== "false";
  });
  const [originalPdfUrl, setOriginalPdfUrl] = useState<string | null>(null);
  const [hasHighlightedPdf, setHasHighlightedPdf] = useState<boolean>(false);
  // Drives ONLY the toolbar toggle-button visibility — independent of
  // `hasHighlightedPdf` (which drives `activePdfUrl` for the head report).
  // In continuous-scroll, this tracks whichever report is currently centered
  // in the viewport, so the button reflects the visible PDF without ever
  // re-deriving `activePdfUrl` (which would tear down the scroll session).
  const [currentReportHasHighlights, setCurrentReportHasHighlights] =
    useState<boolean>(false);

  /** Toggle cell-refs and persist as a global user preference.
   * Used by both single-PDF and merged-PDF toolbar buttons so the choice
   * is remembered across reports, weeks, and sessions. */
  const persistCellRefsEnabled = useCallback(
    async (next: boolean) => {
      if (!userConfig?.id) return;
      // SetUserProfileInput requires default_team_id (Int!) per schema.
      // Read the user's existing default off userConfig (the field is missing
      // from the generated User type) and fall back to currentTeamId so we
      // don't accidentally change the user's default team while persisting
      // an unrelated preference.
      const existingDefault =
        (userConfig as unknown as { default_team_id?: number | null })
          .default_team_id ?? currentTeamId;
      if (existingDefault == null) return;
      try {
        const current = parseUserAttributes(userConfig);
        const updated = { ...current, cell_refs_enabled: next };
        await client.graphql({
          query: `
            mutation SetUserProfile($input: SetUserProfileInput!) {
              setUserProfile(input: $input) { id attributes }
            }
          `,
          variables: {
            input: {
              id: userConfig.id,
              default_team_id: existingDefault,
              attributes: JSON.stringify(updated),
            },
          },
        });
      } catch (err) {
        console.warn("Failed to persist cell_refs_enabled:", err);
      }
    },
    // client is stable per render but ESLint can't see that — cast to deps anyway
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userConfig, currentTeamId]
  );

  // After a cell-refs toggle the activePdfUrl effect re-renders every
  // appended report. We capture the user's scroll target here so we can
  // restore the viewport once the re-render finishes.
  const pendingScrollAfterToggleRef = useRef<{
    activeKey: string;
    page: number;
  } | null>(null);

  const toggleCellRefs = useCallback(
    (next: boolean) => {
      // Capture scroll target so the post-toggle reload can land the user
      // back where they were (active report + current page) instead of
      // jumping to the head report's first page.
      const activeKey = activeReportKeyRef.current;
      if (activeKey) {
        pendingScrollAfterToggleRef.current = {
          activeKey,
          page: currentPage,
        };
      }
      setCellRefsEnabled(next);
      persistCellRefsEnabled(next);
    },
    [currentPage, persistCellRefsEnabled]
  );

  const isScrollSyncRef = useRef<boolean>(false);
  const scrollCleanupRef = useRef<(() => void) | null>(null);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [pillStyle, setPillStyle] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isDraggingRef = useRef<boolean>(false);
  const pdfZoomRef = useRef<number>(pdfZoom); // track zoom without re-triggering load
  pdfZoomRef.current = pdfZoom;

  // Memoize the Amplify client so its identity is stable across renders.
  // generateClient() returns a new object each call; an unstable client made
  // fetchCalloutSummary (useCallback [client]) — and the summary-fetch effect
  // that depends on it — re-fire every render, causing an infinite
  // getCalloutSummary request loop.
  const client = useMemo(() => generateAuditedClient(), []);

  // Unified callout state (wbr-unified-callouts spec — task 11.3)
  // The hook is called with no report_name so the same `callouts` array serves
  // both detail view (filter to selectedReport.filename) and merged view.
  // `readonlyFallback` is the legacy `_callouts.html`-parsed list that fires
  // when getCallouts is empty AND UNIFIED_CALLOUT_FALLBACK=true (Req 12.3).
  const {
    callouts: unifiedCallouts,
    readonlyFallback,
    addCallout: addUnifiedCallout,
    updateCallout: updateUnifiedCallout,
    deleteCallout: deleteUnifiedCallout,
    error: mcError,
  } = useUnifiedCallouts({
    teamId: currentTeamId,
    week: weekToParam(selectedWeek.year, selectedWeek.week),
    reportName: undefined,
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editorCallout, setEditorCallout] = useState<Callout | undefined>(
    undefined
  );
  const [editorPageNum, setEditorPageNum] = useState(1);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleteSnackbar, setDeleteSnackbar] = useState<string | null>(null);

  // All callouts (AI + manual) for the currently selected report — subset of
  // `unifiedCallouts`. Used by both AI and manual rendering paths so every
  // per-callout card flows through `<CalloutCard>` (Decision #14, Req 8.3).
  // Sorted by card number ascending so the list order matches the numbered
  // badge shown on each card.
  // When `selectedReport` is null (merged view / folder summary), this returns
  // every callout for the (team, week).
  const reportCallouts = useMemo(() => {
    const filtered = unifiedCallouts.filter(
      (c) => !selectedReport || c.report_name === selectedReport.filename
    );
    return filtered.slice().sort(compareByCardNumber);
  }, [unifiedCallouts, selectedReport]);

  // Latest AI-generation timestamp, derived from unified callouts. Used by
  // the stale-indicator: a manual callout is stale if its updated_at predates
  // the latest AI batch's created_at.
  const aiGenTimestamp = useMemo<string | null>(() => {
    let latest: string | null = null;
    unifiedCallouts.forEach((c) => {
      if (c.source !== "ai") return;
      if (!selectedReport || c.report_name === selectedReport.filename) {
        if (!latest || c.created_at > latest) latest = c.created_at;
      }
    });
    return latest;
  }, [unifiedCallouts, selectedReport]);

  // Per-page filter helper for the unified callout array. Used by the mobile
  // and desktop per-page render paths to drive `<CalloutCard>` directly from
  // the unified hook (Decision #14, Req 8.3 / 9.2). Sorted by card number
  // ascending so the list order matches the numbered badge on each card.
  const calloutsForPage = useMemo(
    () =>
      (pageNum: number, reportFilename?: string): Callout[] =>
        unifiedCallouts
          .filter(
            (c) =>
              c.page_num === pageNum &&
              (!reportFilename || c.report_name === reportFilename)
          )
          .sort(compareByCardNumber),
    [unifiedCallouts]
  );

  // Merged-view sections derived from `pageOffsetMap` + `unifiedCallouts`.
  // We only emit a section for each (remapped page, source report) pair where
  // at least one callout exists. Callouts inside each section come from the
  // unified hook (AI + manual) — see Decision #14: every per-callout card is
  // a `<CalloutCard>`, never raw HTML.
  const mergedCalloutData = useMemo<MergedPageSection[] | null>(() => {
    if (!pageOffsetMap) return null;
    // Group: remappedPage -> reportFilename -> Callout[]
    const grouped = new Map<number, Map<string, Callout[]>>();
    for (const c of unifiedCallouts) {
      const info = pageOffsetMap[c.report_name];
      if (!info) continue; // callout's report isn't part of this merge
      const remapped = c.page_num + (info.start_page - 1);
      let byReport = grouped.get(remapped);
      if (!byReport) {
        byReport = new Map<string, Callout[]>();
        grouped.set(remapped, byReport);
      }
      const arr = byReport.get(c.report_name) ?? [];
      arr.push(c);
      byReport.set(c.report_name, arr);
    }
    const sections: MergedPageSection[] = [];
    for (const [remapped, byReport] of grouped) {
      for (const [reportFilename, calls] of byReport) {
        const info = pageOffsetMap[reportFilename];
        // Sort callouts within the section by card number ascending so the
        // list order matches the numbered badge on each card.
        const sorted = calls.slice().sort(compareByCardNumber);
        sections.push({
          page_number: remapped,
          source_report: info.showing_name,
          source_filename: reportFilename,
          title: `Page ${remapped}`,
          callouts: sorted,
        });
      }
    }
    sections.sort((a, b) => {
      if (a.page_number !== b.page_number) return a.page_number - b.page_number;
      return a.source_report.localeCompare(b.source_report);
    });
    return sections;
  }, [pageOffsetMap, unifiedCallouts]);

  /** Check if the current user can edit a callout. AI rows are owned by the
   *  system user (id=-1) and are editable by admin/operator only. */
  const canEditCallout = (callout: Callout): boolean => {
    if (readonlyFallback) return false;
    const effectiveRole = effectiveRoleProp ?? "user";
    const currentUserId = userConfig?.id ?? 0;
    const isFinnTeamAdmin =
      currentTeamId === 1 &&
      (effectiveRole === "admin" || effectiveRole === "operator");
    if (
      effectiveRole === "admin" ||
      effectiveRole === "operator" ||
      isFinnTeamAdmin
    )
      return true;
    return callout.creator_id === currentUserId;
  };

  /** Whether the current user may upload WBR PDFs for the active team.
   *  Admin-only (team admin OR FINN-team (id 1) admin). "operator" is NOT
   *  sufficient — stricter than callout editing. Group-admin is enforced
   *  server-side (the client only knows the effective role for the current
   *  team); the Lambda re-verifies all three admin senses regardless. */
  const canUploadWbrPdf: boolean = (() => {
    if (readonlyFallback) return false;
    const effectiveRole = effectiveRoleProp ?? "user";
    if (effectiveRole === "admin") return true; // team admin or FINN-team admin
    return false;
  })();

  // ── edit-callout-summary: Narrative permission + fetch + edit handlers ──
  /**
   * Whether the current user may edit the Narrative owned by `narrativeTeamId`.
   * Mirrors the canEditCallout / wbr-unified-callouts Req 8 permission model:
   * same-team admin/operator, or FINN-team (team_id=1) admin/operator
   * cross-team; user role denied (Req 5.1, 5.2, 5.5, 5.6).
   *
   * Delegates to the pure `canEditSummaryDecision` helper (single source of
   * truth, unit/property testable).
   */
  const canEditSummary = useCallback(
    (narrativeTeamId: number | null): boolean =>
      canEditSummaryDecision({
        readonlyFallback,
        effectiveRole: effectiveRoleProp ?? "user",
        currentTeamId,
        narrativeTeamId,
      }),
    [readonlyFallback, effectiveRoleProp, currentTeamId]
  );

  /**
   * Read the latest persisted Narrative for a (team_id, week, report_name) from
   * the Callout_Summary_Store. Resolves with the CalloutSummary row or null.
   * Throws on transport failure so callers can surface a load error (Req 1.4).
   */
  const fetchCalloutSummary = useCallback(
    async (
      teamId: number,
      week: string,
      reportName: string
    ): Promise<CalloutSummary | null> => {
      const result = await client.graphql({
        query: getCalloutSummary,
        variables: {
          input: { team_id: teamId, week, report_name: reportName },
        },
      });
      return (result as any)?.data?.getCalloutSummary ?? null;
    },
    [client]
  );

  // Load the Narrative in parallel with the unified callouts whenever the
  // displayed (team, week, report) changes (Req 1.1, 11.1). The banner/key-table
  // derive from the unified callouts the useUnifiedCallouts hook already loads.
  useEffect(() => {
    const reportName = selectedReport?.filename;
    const week = weekToParam(selectedWeek.year, selectedWeek.week);
    if (!reportName) {
      setSummaryContent(null);
      setSummaryOverview(null);
      setSummaryKpiStrip([]);
      setSummaryKeyCallouts([]);
      setSummaryUpdatedAt(null);
      setSummaryTeamId(null);
      setSummaryError(null);
      setSummaryLoading(false);
      return;
    }
    let cancelled = false;
    setSummaryLoading(true);
    setSummaryError(null);
    (async () => {
      try {
        const row = await fetchCalloutSummary(currentTeamId, week, reportName);
        if (cancelled) return;
        setSummaryContent(row?.content ?? null);
        setSummaryOverview(row?.overview ?? null);
        setSummaryKpiStrip(row?.kpi_strip ?? []);
        setSummaryKeyCallouts(row?.key_callouts ?? []);
        setSummaryUpdatedAt(row?.updated_at ?? null);
        setSummaryTeamId(row?.team_id ?? null);
      } catch (err) {
        if (cancelled) return;
        // A read failure leaves the section without an editable Narrative;
        // the banner/key-table still derive from callouts.
        setSummaryContent(null);
        setSummaryOverview(null);
        setSummaryKpiStrip([]);
        setSummaryKeyCallouts([]);
        setSummaryUpdatedAt(null);
        setSummaryTeamId(null);
        setSummaryError("Could not load the summary narrative.");
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedReport, selectedWeek, currentTeamId, fetchCalloutSummary]);

  /**
   * Open the Summary_Editor. Refetches the latest persisted content first; on
   * success opens pre-populated, on failure keeps the section unchanged, does
   * not open, and surfaces a load error (Req 1.3, 1.4, 2.1, 2.2).
   */
  const handleOpenSummaryEditor = useCallback(async () => {
    const reportName = selectedReport?.filename;
    if (!reportName || summaryEditorOpen) return;
    const week = weekToParam(selectedWeek.year, selectedWeek.week);
    setSummaryError(null);
    try {
      const row = await fetchCalloutSummary(currentTeamId, week, reportName);
      // Modify-only: no Narrative means nothing to edit (Req 3.6).
      if (!row || (row.content == null && !row.overview)) {
        setSummaryContent(null);
        setSummaryOverview(null);
        setSummaryKpiStrip([]);
        setSummaryKeyCallouts([]);
        setSummaryError("Could not load the narrative for editing.");
        return;
      }
      setSummaryContent(row.content ?? null);
      setSummaryOverview(row.overview ?? null);
      setSummaryKpiStrip(row.kpi_strip ?? []);
      setSummaryKeyCallouts(row.key_callouts ?? []);
      setSummaryUpdatedAt(row.updated_at ?? null);
      setSummaryTeamId(row.team_id ?? null);
      setSummaryEditorOpen(true);
    } catch (err) {
      // Keep the section unchanged; do not open; show a load error (Req 1.4).
      setSummaryError("Could not load the narrative for editing.");
    }
  }, [
    selectedReport,
    selectedWeek,
    currentTeamId,
    summaryEditorOpen,
    fetchCalloutSummary,
  ]);

  /** Cancel the editor — stored content stays unchanged; render pre-edit content (Req 8.5). */
  const handleCancelSummaryEditor = useCallback(() => {
    setSummaryEditorOpen(false);
    setSummaryError(null);
  }, []);

  /**
   * Optimistic save: capture the pre-edit content, render the edit immediately,
   * call updateCalloutSummary, and on success replace with the returned
   * sanitized content (Req 7.1, 7.3). On failure (validation, network, or >10s
   * timeout) roll back within 1s, keep the editor open with the user's text,
   * and surface an error (Req 8.1, 8.2, 8.4).
   */
  const handleSaveSummary = useCallback(
    async (
      overview: string,
      kpiStrip: KpiCell[],
      keyCallouts: KeyCalloutRef[]
    ) => {
      const reportName = selectedReport?.filename;
      if (!reportName) return;
      const week = weekToParam(selectedWeek.year, selectedWeek.week);
      const previousOverview = summaryOverview;
      const previousKpiStrip = summaryKpiStrip;
      const previousKeyCallouts = summaryKeyCallouts;
      const previous = summaryContent;
      setPrevSummaryContent(previous);
      setSummaryError(null);
      setSummarySaving(true);
      // Optimistic render of the edited structured data.
      setSummaryOverview(overview);
      setSummaryKpiStrip(kpiStrip);
      setSummaryKeyCallouts(keyCallouts);
      try {
        const TIMEOUT_MS = 10000;
        const mutationPromise = client.graphql({
          query: updateCalloutSummary,
          variables: {
            input: {
              team_id: currentTeamId,
              week,
              report_name: reportName,
              overview,
              kpi_strip: kpiStrip.map(({ label, value, sub, color }) => ({
                label,
                value,
                sub,
                ...(color ? { color } : {}),
              })),
              key_callouts: keyCallouts.map(({ page_num, card_number }) => ({
                page_num,
                card_number,
              })),
              updated_by: userConfig?.id ?? 0,
            },
          },
        }) as Promise<any>;
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("timeout")), TIMEOUT_MS)
        );
        const result = await Promise.race([mutationPromise, timeoutPromise]);
        const saved: CalloutSummary | null =
          result?.data?.updateCalloutSummary ?? null;
        if (!saved) {
          throw new Error("Update did not return saved content.");
        }
        // Replace optimistic content with the server's sanitized content.
        setSummaryContent(saved.content ?? null);
        setSummaryOverview(saved.overview ?? null);
        setSummaryKpiStrip(saved.kpi_strip ?? []);
        setSummaryKeyCallouts(saved.key_callouts ?? []);
        setSummaryUpdatedAt(saved.updated_at ?? null);
        setSummaryTeamId(saved.team_id ?? null);
        setSummaryEditorOpen(false);
      } catch (err) {
        // Roll back the optimistic render and keep the editor open.
        setSummaryOverview(previousOverview);
        setSummaryKpiStrip(previousKpiStrip);
        setSummaryKeyCallouts(previousKeyCallouts);
        setSummaryContent(previous ?? prevSummaryContent);
        setSummaryError(
          "The save did not succeed. The previously stored narrative remains unchanged."
        );
      } finally {
        setSummarySaving(false);
      }
    },
    [
      selectedReport,
      selectedWeek,
      currentTeamId,
      summaryContent,
      summaryOverview,
      summaryKpiStrip,
      summaryKeyCallouts,
      prevSummaryContent,
      userConfig,
      client,
    ]
  );

  /** Check if a manual callout is stale (predates latest AI generation) */
  const isStaleCallout = (
    callout: Callout,
    aiGenTs: string | null
  ): boolean => {
    if (!aiGenTs) return false;
    // AI callouts are never stale — they ARE the latest generation
    if (callout.source === "ai") return false;
    const calloutTime = new Date(callout.updated_at || callout.created_at);
    const genTime = new Date(aiGenTs);
    return calloutTime < genTime;
  };

  // Initialize cell refs toggle from user setting
  // Skip initialization if ?cellRefs= URL param is present so shareable links
  // with an explicit cell-refs value win over the user's saved preference.
  const cellRefsFromUrl = useRef<boolean>(
    new URLSearchParams(window.location.search).has("cellRefs")
  );
  useEffect(() => {
    if (cellRefsFromUrl.current) return;
    if (userConfig) {
      const attributes = parseUserAttributes(userConfig);
      setCellRefsEnabled(attributes.cell_refs_enabled ?? true);
    }
  }, [userConfig]);

  // Derive the active PDF URL based on toggle state
  const activePdfUrl = useMemo(() => {
    if (cellRefsEnabled && hasHighlightedPdf) {
      return sourcePdfUrl; // highlighted version
    }
    return originalPdfUrl ?? sourcePdfUrl; // original or fallback
  }, [cellRefsEnabled, hasHighlightedPdf, sourcePdfUrl, originalPdfUrl]);

  // Derive the active merged PDF URL based on toggle state
  const activeMergedPdfUrl = useMemo(() => {
    if (cellRefsEnabled && mergedHighlightedPdfUrl) {
      return mergedHighlightedPdfUrl;
    }
    return mergedPdfUrl;
  }, [cellRefsEnabled, mergedHighlightedPdfUrl, mergedPdfUrl]);

  // Whether any report in the merged view has highlights
  const mergedHasHighlights = useMemo(() => {
    if (!highlightMap) return false;
    return Object.values(highlightMap).some(Boolean);
  }, [highlightMap]);

  // Track whether we're building the highlighted merge on demand
  const [buildingHighlightedMerge, setBuildingHighlightedMerge] =
    useState<boolean>(false);

  // Build the merged highlighted PDF on demand when user toggles cell refs ON
  // or when the merged view loads with cell refs already enabled.
  const buildHighlightedMerge = useCallback(async () => {
    if (mergedHighlightedPdfUrl || buildingHighlightedMerge) return;
    setBuildingHighlightedMerge(true);
    try {
      const response = await client.graphql({
        query: getWBRCallouts,
        variables: {
          input: {
            team_id: currentTeamId,
            team_name: currentTeamName,
            week: weekToParam(selectedWeek.year, selectedWeek.week),
            build_highlighted: true,
          },
        },
      });
      const data = response.data.getWBRCallouts;
      if (data?.success && data.merged_highlighted_pdf_url) {
        setMergedHighlightedPdfUrl(data.merged_highlighted_pdf_url);
      }
    } catch (err) {
      console.error("Failed to build highlighted merge:", err);
    } finally {
      setBuildingHighlightedMerge(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentTeamId,
    currentTeamName,
    selectedWeek,
    mergedHighlightedPdfUrl,
    buildingHighlightedMerge,
  ]);

  // Auto-build the merged highlighted PDF when entering merged view with
  // cell refs enabled. Without this, the first render of the merged view
  // shows the non-highlighted PDF because the Lambda only builds highlights
  // on-demand (when build_highlighted=true). The user would have to click
  // the toggle twice to see the highlighted version.
  useEffect(() => {
    if (
      isMergedView &&
      cellRefsEnabled &&
      mergedHasHighlights &&
      !mergedHighlightedPdfUrl &&
      !buildingHighlightedMerge &&
      !mergedLoading
    ) {
      buildHighlightedMerge();
    }
  }, [
    isMergedView,
    cellRefsEnabled,
    mergedHasHighlights,
    mergedHighlightedPdfUrl,
    buildingHighlightedMerge,
    mergedLoading,
    buildHighlightedMerge,
  ]);

  // Fetch report list on mount / team change / week change
  const fetchReportList = useCallback(async () => {
    if (!currentTeamId || currentTeamId <= 0) return;
    setLoading(true);
    setError(null);
    try {
      const input: GetWBRCalloutsInput = {
        team_id: currentTeamId,
        team_name: currentTeamName,
        week: weekToParam(selectedWeek.year, selectedWeek.week),
      };
      const result = await client.graphql({
        query: getWBRCallouts,
        variables: { input },
      });
      const response = result.data.getWBRCallouts;
      if (response?.success) {
        setReportList(response.reports ?? []);
        setFolderSummaryUrl(response.folder_summary_url ?? null);
      } else {
        setError(response?.message ?? "Failed to load reports");
        setFolderSummaryUrl(null);
      }
    } catch (err: any) {
      console.error("Error fetching WBR report list:", err);
      setError(err?.message ?? "Failed to load WBR reports");
      setFolderSummaryUrl(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeamId, selectedWeek]);

  /** Given the source PDF file names just uploaded, return the set of those
   *  whose callouts have been generated. Authoritative: the getWbrCalloutStatus
   *  query head_objects {team}/{week}/PDF/split/{name}_callouts.html in the
   *  callout bucket (wbr-callout-{stage}) — the file the generator writes on
   *  completion. Drives the callout-generation progress in the upload panel. */
  const fetchReadyFilenames = useCallback(
    async (fileNames: string[]): Promise<Set<string>> => {
      if (!currentTeamId || currentTeamId <= 0 || fileNames.length === 0) {
        return new Set();
      }
      try {
        const input = {
          team_id: currentTeamId,
          week: formatWeekEndDate(selectedWeek.endDate),
          file_names: fileNames,
        };
        const result = await client.graphql({
          query: getWbrCalloutStatus,
          variables: { input },
        });
        const statuses = result.data.getWbrCalloutStatus?.statuses ?? [];
        return new Set(
          statuses
            .filter((s) => s?.ready)
            .map((s) => (s.file_name ?? "").toLowerCase())
            .filter(Boolean)
        );
      } catch (err) {
        console.warn("fetchReadyFilenames (callout status) failed:", err);
        return new Set();
      }
    },
    [client, currentTeamId, selectedWeek]
  );

  useEffect(() => {
    if (isOpen && currentTeamId && currentTeamId > 0) fetchReportList();
  }, [isOpen, currentTeamId, fetchReportList]);

  // Reset when team changes
  useEffect(() => {
    setSelectedReport(null);
    // Clear the report list so the auto-select-first-report effect doesn't
    // fire with the previous team's reports while the new team's list is
    // still being fetched.
    setReportList([]);
    setFolderSummaryUrl(null);
    setFolderSummaryHtml(null);
    setFolderSummaryError(null);
    setSourcePdfUrl(null);
    setCalloutHtmlUrl(null);
    setOriginalPdfUrl(null);
    setHasHighlightedPdf(false);
    setCurrentReportHasHighlights(false);
    setCalloutPages([]);
    setCalloutStyles("");
    setExecSummaryHtml("");
    setCurrentPage(1);
    setTotalPdfPages(0);
    setRightTab(0);
    // Clear merged-view state so the auto-select-first-report effect isn't
    // blocked by lingering isMergedView=true from the previous team. Without
    // this, switching teams while in merged view (or with stale appended
    // reports) skips the auto-load of the new team's first PDF.
    setIsMergedView(false);
    setMergedPdfUrl(null);
    setMergedHighlightedPdfUrl(null);
    setHighlightMap(null);
    setMergedError(null);
    setPageOffsetMap(null);
    setSkippedFiles(null);
    setAppendedReports([]);
    setAppendingNext(false);
    setAppendingPrev(false);
    appendingNextRef.current = false;
    appendingPrevRef.current = false;
    pdfDocRef.current = null;
    highlightedByReportRef.current.clear();
    calloutByReportRef.current.clear();
    pdfDocByReportRef.current.clear();
    pdfUrlsByReportRef.current.clear();
    inFlightPrefetchRef.current.clear();
    if (pdfCanvasContainerRef.current) {
      pdfCanvasContainerRef.current.innerHTML = "";
    }
  }, [currentTeamId]);

  // Fetch folder summary HTML when the pre-signed URL changes
  useEffect(() => {
    if (!folderSummaryUrl) {
      setFolderSummaryHtml(null);
      return;
    }
    let cancelled = false;
    setFolderSummaryLoading(true);
    setFolderSummaryError(null);

    (async () => {
      try {
        const resp = await fetch(folderSummaryUrl);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const html = await resp.text();
        if (!cancelled) setFolderSummaryHtml(html);
      } catch (err: any) {
        if (!cancelled)
          setFolderSummaryError(err?.message ?? "Failed to load summary");
      } finally {
        if (!cancelled) setFolderSummaryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [folderSummaryUrl]);

  // Fetch detail (pre-signed URLs) when a report is selected
  const handleReportSelect = useCallback(
    async (report: WBRReportMeta) => {
      setSelectedReport(report);
      setIsMergedView(false);
      setSourcePdfUrl(null);
      setCalloutHtmlUrl(null);
      setOriginalPdfUrl(null);
      setHasHighlightedPdf(false);
      setCurrentReportHasHighlights(false);
      setCalloutPages([]);
      setCalloutStyles("");
      setExecSummaryHtml("");
      setCurrentPage(1);
      setTotalPdfPages(0);
      setRightTab(0);
      // Reset continuous-scroll: a new report becomes the head of the queue.
      setAppendedReports([report]);
      setAppendingNext(false);
      setAppendingPrev(false);
      appendingNextRef.current = false;
      appendingPrevRef.current = false;
      calloutByReportRef.current.clear();
      pdfDocByReportRef.current.clear();
      highlightedByReportRef.current.clear();
      pdfUrlsByReportRef.current.clear();
      inFlightPrefetchRef.current.clear();
      appendTokenRef.current += 1;
      pdfDocRef.current = null;
      if (pdfCanvasContainerRef.current) {
        pdfCanvasContainerRef.current.innerHTML = "";
      }
      // (URL sync handled by the central writeShareUrl effect)
      setDetailLoading(true);
      setError(null);
      setReportMenuAnchor(null);
      try {
        const input: GetWBRCalloutsInput = {
          team_id: currentTeamId,
          team_name: currentTeamName,
          report_name: report.filename,
          week: weekToParam(selectedWeek.year, selectedWeek.week),
        };
        const result = await client.graphql({
          query: getWBRCallouts,
          variables: { input },
        });
        const response = result.data.getWBRCallouts;
        if (
          response?.success &&
          response.reports &&
          response.reports.length > 0
        ) {
          const detail = response.reports[0];
          // Store both URLs for cell-refs toggle
          const highlightedUrl = detail.source_pdf_url ?? null;
          const originalUrl = detail.original_pdf_url ?? null;
          const isHighlighted = detail.highlighted === true;

          setSourcePdfUrl(highlightedUrl);
          setOriginalPdfUrl(originalUrl);
          setHasHighlightedPdf(isHighlighted);
          setCurrentReportHasHighlights(isHighlighted);
          highlightedByReportRef.current.set(report.filename, isHighlighted);
          pdfUrlsByReportRef.current.set(report.filename, {
            highlighted: highlightedUrl,
            original: originalUrl,
            isHighlighted,
          });
          setCalloutHtmlUrl(detail.callout_html_url ?? null);

          // Manual callouts and AI generation timestamp now come from the
          // useUnifiedCallouts hook, which auto-loads on (teamId, week) change.
          // Nothing to do here — the legacy manual_callouts/ai_generation_timestamp
          // fields were removed from the GraphQL response.
        } else {
          setError(response?.message ?? "Failed to load report detail");
        }
      } catch (err: any) {
        console.error("Error fetching report detail:", err);
        setError(err?.message ?? "Failed to load report detail");
      } finally {
        setDetailLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTeamId, currentTeamName, selectedWeek]
  );

  // ── Auto-refresh when callouts finish generating ──────────────────────
  //
  // A freshly-uploaded report shows its source PDF immediately, but callout
  // generation (and the highlighted PDF) runs asynchronously and lands in the
  // callout bucket minutes later. handleReportSelect fetches detail ONCE and
  // caches the result, so a report opened before generation completes would
  // otherwise stay stuck on "Callout data not available" until a full reload.
  //
  // This lightweight refetch re-runs detail mode for the currently-open report
  // and updates only the URL state — the calloutHtmlUrl effect then parses and
  // renders. It intentionally avoids the full teardown in handleReportSelect so
  // the user's current page / tab / scroll position are preserved.
  const refetchOpenReportDetail = useCallback(
    async (report: WBRReportMeta) => {
      try {
        const input: GetWBRCalloutsInput = {
          team_id: currentTeamId,
          team_name: currentTeamName,
          report_name: report.filename,
          week: weekToParam(selectedWeek.year, selectedWeek.week),
        };
        const result = await client.graphql({
          query: getWBRCallouts,
          variables: { input },
        });
        const response = result.data.getWBRCallouts;
        if (
          response?.success &&
          response.reports &&
          response.reports.length > 0
        ) {
          const detail = response.reports[0];
          const highlightedUrl = detail.source_pdf_url ?? null;
          const originalUrl = detail.original_pdf_url ?? null;
          const isHighlighted = detail.highlighted === true;
          // Drop any cached (stale-null) callout parse for this report so the
          // calloutHtmlUrl effect re-fetches and re-parses the new HTML.
          calloutByReportRef.current.delete(report.filename);
          pdfUrlsByReportRef.current.set(report.filename, {
            highlighted: highlightedUrl,
            original: originalUrl,
            isHighlighted,
          });
          highlightedByReportRef.current.set(report.filename, isHighlighted);
          setSourcePdfUrl(highlightedUrl);
          setOriginalPdfUrl(originalUrl);
          setHasHighlightedPdf(isHighlighted);
          setCurrentReportHasHighlights(isHighlighted);
          setCalloutHtmlUrl(detail.callout_html_url ?? null);
        }
      } catch (err) {
        console.warn("refetchOpenReportDetail failed:", err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, currentTeamId, currentTeamName, selectedWeek]
  );

  // Poll for callout readiness while a report is open without callouts yet.
  // Stops as soon as the callout HTML appears (refetched above), on report/
  // week/team change, or after a safety timeout.
  useEffect(() => {
    // Only poll when a single report is open, not in merged view, and its
    // callouts have not loaded yet.
    if (!selectedReport || isMergedView || calloutHtmlUrl) return;
    if (!currentTeamId || currentTeamId <= 0) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();
    const POLL_INTERVAL_MS = 12000;
    const POLL_TIMEOUT_MS = 8 * 60000; // 8 minutes

    const filename = selectedReport.filename.toLowerCase();
    setCalloutGenerating(true);

    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setCalloutGenerating(false); // give up quietly
        return;
      }
      try {
        const ready = await fetchReadyFilenames([filename]);
        if (cancelled) return;
        if (ready.has(filename)) {
          await refetchOpenReportDetail(selectedReport);
          setCalloutGenerating(false);
          return; // calloutHtmlUrl now set → effect deps change → poll stops
        }
      } catch {
        // transient — keep polling until the timeout
      }
      if (!cancelled) timeoutId = setTimeout(tick, POLL_INTERVAL_MS);
    };

    // First check after one interval (generation is never instant).
    timeoutId = setTimeout(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      setCalloutGenerating(false);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    selectedReport,
    isMergedView,
    calloutHtmlUrl,
    currentTeamId,
    selectedWeek,
    fetchReadyFilenames,
    refetchOpenReportDetail,
  ]);

  // Auto-select report from ?report= URL param once the report list loads
  useEffect(() => {
    if (!pendingReport || reportList.length === 0) return;
    const match = reportList.find(
      (r) =>
        r.filename === pendingReport || r.filename.startsWith(pendingReport)
    );
    if (match) {
      handleReportSelect(match);
      // handleReportSelect resets rightTab to 0 (exec summary). Re-apply the
      // ?tab= URL param so shareable links with tab=callouts are honored.
      const params = new URLSearchParams(window.location.search);
      if (params.get("tab") === "callouts") {
        setRightTab(1);
      }
    }
    setPendingReport(null); // consume it — only auto-select once
  }, [reportList, pendingReport, handleReportSelect]);

  // Default landing experience: the first thing a user wants to see when
  // opening the WBR Callouts viewer is the first report's PDF, not the
  // folder-level executive summary. Auto-select the first report whenever
  // the report list loads and no other selection (URL deep-link, manual
  // pick, merged view) is pending or active.
  useEffect(() => {
    if (reportList.length === 0) return;
    if (pendingReport || pendingMerged) return; // URL deep-link wins
    if (selectedReport || isMergedView) return; // user has already chosen
    handleReportSelect(reportList[0]);
  }, [
    reportList,
    pendingReport,
    pendingMerged,
    selectedReport,
    isMergedView,
    handleReportSelect,
  ]);

  // Manual callout handlers
  const handleAddCallout = (pageNum: number) => {
    setEditorMode("create");
    setEditorCallout(undefined);
    setEditorPageNum(pageNum);
    setEditorOpen(true);
  };

  const handleEditCallout = (callout: Callout) => {
    setEditorMode("edit");
    setEditorCallout(callout);
    setEditorPageNum(callout.page_num);
    setEditorOpen(true);
  };

  const handleDeleteCallout = async (calloutId: number) => {
    const userId = userConfig?.id ?? 0;
    const ok = await deleteUnifiedCallout({ id: calloutId, user_id: userId });
    if (!ok) {
      setDeleteSnackbar(mcError ?? "Failed to delete callout");
    }
    setDeleteConfirmId(null);
  };

  const handleSaveCallout = async (callout: Callout) => {
    if (editorMode === "create") {
      const input: AddCalloutInput = {
        team_id: currentTeamId,
        week: weekToParam(selectedWeek.year, selectedWeek.week),
        report_name: selectedReport?.filename ?? "",
        page_num: editorPageNum,
        title: callout.title,
        status: callout.status,
        trend: callout.trend,
        why_text: callout.why_text,
        cell_refs: callout.cell_refs.map((r) => ({ row: r.row, col: r.col })),
        creator_id: userConfig?.id ?? 0,
      };
      const result = await addUnifiedCallout(input);
      if (result) setEditorOpen(false);
    } else {
      const input: UpdateCalloutInput = {
        id: callout.id,
        title: callout.title,
        status: callout.status,
        trend: callout.trend,
        why_text: callout.why_text,
        impact: callout.impact,
        cell_refs: callout.cell_refs.map((r) => ({ row: r.row, col: r.col })),
        updated_by: userConfig?.id ?? 0,
      };
      const result = await updateUnifiedCallout(input);
      if (result) setEditorOpen(false);
    }
  };

  // Enter merged view — fetch merged PDF URL + offset map, then fetch callout HTMLs client-side
  const handleEnterMergedView = useCallback(async () => {
    setIsMergedView(true);
    setMergedLoading(true);
    setMergedError(null);
    setSelectedReport(null);
    setImpactFilter("all");

    try {
      const response = await client.graphql({
        query: getWBRCallouts,
        variables: {
          input: {
            team_id: currentTeamId,
            team_name: currentTeamName,
            week: weekToParam(selectedWeek.year, selectedWeek.week),
            merged: true,
          },
        },
      });

      const data = response.data.getWBRCallouts;
      if (!data?.success) {
        setMergedError(data?.message ?? "Failed to generate merged view");
        return;
      }

      setMergedPdfUrl(data.merged_pdf_url ?? null);
      setSkippedFiles(data.skipped_files ?? null);

      const offsetMap: PageOffsetMap | null = data.page_offset_map
        ? (() => {
            let parsed = JSON.parse(data.page_offset_map);
            if (typeof parsed === "string") parsed = JSON.parse(parsed);
            return parsed;
          })()
        : null;

      setMergedHighlightedPdfUrl(data.merged_highlighted_pdf_url ?? null);
      if (data.highlight_map) {
        let hlMap: Record<string, boolean> = JSON.parse(data.highlight_map);
        if (typeof hlMap === "string") hlMap = JSON.parse(hlMap);
        setHighlightMap(hlMap);
      } else {
        setHighlightMap(null);
      }
      setPageOffsetMap(offsetMap);

      if (!offsetMap) {
        return;
      }

      // Fetch each callout HTML in parallel solely to surface fetch errors
      // and tolerate skipped files. The actual merged sections are derived
      // from `pageOffsetMap` + `unifiedCallouts` via the `mergedCalloutData`
      // memo above (Decision #14 — every per-callout card flows through
      // `<CalloutCard>`, not raw HTML).
      const skipped: string[] = [...(data.skipped_files ?? [])];

      await Promise.all(
        Object.entries(offsetMap).map(async ([pdfFilename, info]) => {
          const url = (info as any).callout_html_url;
          if (!url) return;
          try {
            const resp = await fetch(url);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          } catch (err) {
            console.warn(
              `Failed to fetch callout HTML for ${pdfFilename}:`,
              err
            );
            skipped.push(pdfFilename);
          }
        })
      );

      if (skipped.length > (data.skipped_files?.length ?? 0)) {
        setSkippedFiles(skipped);
      }

      // Manual callouts now come from the useUnifiedCallouts hook. The merged
      // view filters `unifiedCallouts` client-side by report_name (against the
      // merged set) — see reportCallouts memo above. The Lambda still remaps
      // page numbers in the page_offset_map; client-side remapping happens in
      // the renderer (search for Page_Offset_Map below).
    } catch (err: any) {
      setMergedError(err?.message ?? "Failed to fetch merged callouts");
    } finally {
      setMergedLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeamId, currentTeamName, selectedWeek]);

  // Auto-enter merged view from ?merged=true URL param once the report list loads
  useEffect(() => {
    if (!pendingMerged || reportList.length === 0) return;
    setPendingMerged(false); // consume it — only auto-enter once
    handleEnterMergedView();
    // handleEnterMergedView resets impactFilter to "all". Re-apply the
    // ?impact= URL param so shareable links with a specific impact are honored.
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("impact");
    if (raw && ["large", "medium", "small", "large+medium"].includes(raw)) {
      setImpactFilter(raw);
    }
  }, [reportList, pendingMerged, handleEnterMergedView]);

  // Load merged PDF document when activeMergedPdfUrl changes (same pattern as activePdfUrl).
  useEffect(() => {
    if (!activeMergedPdfUrl) return;
    let cancelled = false;

    pdfDocRef.current = null;
    if (pdfScrollRef.current) {
      pdfScrollRef.current.innerHTML = "";
    }
    pdfCanvasContainerRef.current = null;
    setTotalPdfPages(0);
    setCurrentPage(1);
    setPdfLoading(true);
    scrollCleanupRef.current?.();
    scrollCleanupRef.current = null;

    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const loadingTask = pdfjsLib.getDocument(activeMergedPdfUrl);
        const pdfDoc = await loadingTask.promise;
        if (cancelled) return;

        pdfDocRef.current = pdfDoc;
        setTotalPdfPages(pdfDoc.numPages);

        // Wait for the scroll container to be mounted
        let retries = 0;
        while (!pdfScrollRef.current && retries < 20) {
          await new Promise((r) => setTimeout(r, 50));
          retries++;
          if (cancelled) return;
        }
        if (!pdfScrollRef.current || cancelled) return;

        await renderPdfPages(pdfDoc, pdfZoomRef.current, () => cancelled);
      } catch (err) {
        console.error("Error loading merged PDF:", err);
        if (!cancelled) setMergedError("Failed to load merged PDF");
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      scrollCleanupRef.current?.();
      scrollCleanupRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMergedPdfUrl]);

  // Fetch and parse callout HTML when URL changes
  useEffect(() => {
    if (!calloutHtmlUrl) return;
    // Skip when this URL belongs to a not-yet-active appended report (cached).
    // The fetch effect runs on every calloutHtmlUrl change, including swaps
    // driven by handleActiveReportChange — those don't need to refetch.
    const cached = selectedReport
      ? calloutByReportRef.current.get(selectedReport.filename)
      : undefined;
    if (cached && cached.calloutHtmlUrl === calloutHtmlUrl) return;

    let cancelled = false;

    (async () => {
      try {
        const resp = await fetch(calloutHtmlUrl);
        const html = await resp.text();
        if (cancelled) return;
        const {
          pages,
          styles,
          execSummaryHtml: execHtml,
        } = parseCalloutPages(html);
        setCalloutPages(pages);
        setCalloutStyles(styles);
        setExecSummaryHtml(execHtml);
        if (selectedReport) {
          calloutByReportRef.current.set(selectedReport.filename, {
            pages,
            styles,
            execSummaryHtml: execHtml,
            calloutHtmlUrl,
          });
        }
        if (pages.length > 0) {
          setCurrentPage(pages[0].pageNumber);
        }
      } catch (fetchErr) {
        console.warn(
          "Direct fetch blocked (CORS), using iframe fallback:",
          fetchErr
        );
        if (cancelled) return;
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = calloutHtmlUrl;
        const onLoad = () => {
          try {
            const doc = iframe.contentDocument;
            if (doc && !cancelled) {
              const html = doc.documentElement.outerHTML;
              const {
                pages,
                styles,
                execSummaryHtml: execHtml,
              } = parseCalloutPages(html);
              setCalloutPages(pages);
              setCalloutStyles(styles);
              setExecSummaryHtml(execHtml);
              if (pages.length > 0) {
                setCurrentPage(pages[0].pageNumber);
              }
            }
          } catch (iframeErr) {
            console.error("Iframe DOM access also blocked.", iframeErr);
          } finally {
            document.body.removeChild(iframe);
          }
        };
        iframe.addEventListener("load", onLoad);
        document.body.appendChild(iframe);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calloutHtmlUrl]);

  // Helper: lazily create the imperative canvas container inside the scroll ref
  const ensureCanvasContainer = useCallback(() => {
    const scrollEl = pdfScrollRef.current;
    if (!scrollEl) return null;
    if (
      pdfCanvasContainerRef.current &&
      !pdfCanvasContainerRef.current.parentNode
    ) {
      pdfCanvasContainerRef.current = null;
    }
    if (!pdfCanvasContainerRef.current) {
      const div = document.createElement("div");
      div.style.width = "fit-content";
      div.style.minWidth = "100%";
      scrollEl.appendChild(div);
      pdfCanvasContainerRef.current = div;
    }
    return pdfCanvasContainerRef.current;
  }, []);

  // Render all pages from an already-loaded pdfDoc into the container.
  // When restoreFraction is provided, that fractional point within
  // restorePage is centered in the viewport. When omitted, the top of
  // restorePage is anchored at the top of the viewport.
  // restoreXFraction (0..1) is the horizontal fraction of the page width
  // to keep at the viewport's horizontal center; needed because zoom can
  // make the canvas wider than the container (horizontal scroll appears).
  const renderPdfPages = useCallback(
    async (
      pdfDoc: any,
      zoom: number,
      cancelled: () => boolean,
      restorePage?: number,
      restoreFraction?: number,
      restoreXFraction?: number,
      reportKey?: string
    ) => {
      const container = ensureCanvasContainer();
      if (!container) return;
      container.innerHTML = "";
      pageCanvasRefs.current.clear();

      const scrollEl = pdfScrollRef.current;
      // Available width for the PDF page (subtract a small padding)
      const containerWidth = scrollEl ? scrollEl.clientWidth - 16 : 800;

      // Phase 1: Create all wrappers + canvases with correct dimensions
      // (no rendering yet) so we can scroll to the right page immediately.
      const pageEntries: {
        page: any;
        canvas: HTMLCanvasElement;
        viewport: any;
      }[] = [];
      const dpr = (window.devicePixelRatio || 1) * 2; // 2x beyond native DPR for maximum sharpness

      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        if (cancelled()) return;

        // Compute a scale that fits the page width to the container,
        // then apply the user's zoom percentage on top.
        const defaultViewport = page.getViewport({ scale: 1 });
        const fitScale = containerWidth / defaultViewport.width;
        const scale = fitScale * (zoom / 100);
        const viewport = page.getViewport({ scale });

        const wrapper = document.createElement("div");
        wrapper.setAttribute("data-page", String(i));
        if (reportKey) wrapper.setAttribute("data-report", reportKey);
        wrapper.style.marginBottom = "8px";
        wrapper.style.marginLeft = "auto";
        wrapper.style.marginRight = "auto";
        wrapper.style.background = "#fff";
        wrapper.style.boxShadow = "0 1px 4px rgba(0,0,0,0.15)";
        wrapper.style.width = "fit-content";

        const canvas = document.createElement("canvas");
        // Render at devicePixelRatio for crisp text on HiDPI/Retina screens
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        // CSS size = logical viewport size; allow it to exceed container width
        // when the user zooms in (the scroll container will scroll horizontally).
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.style.display = "block";

        wrapper.appendChild(canvas);
        container.appendChild(wrapper);
        pageCanvasRefs.current.set(i, canvas);
        pageEntries.push({ page, canvas, viewport });
      }

      if (cancelled() || !scrollEl) return;

      // Scroll to the target page BEFORE rendering content. restoreFraction
      // is the vertical fraction within the page that should land at the
      // viewport center (so zooming keeps the user's focus point steady).
      // When omitted, defaults to anchoring the page top at the viewport top.
      if (restorePage) {
        const target = container.querySelector(
          `[data-page="${restorePage}"]`
        ) as HTMLElement | null;
        if (target) {
          isScrollSyncRef.current = true;
          if (restoreFraction == null) {
            scrollEl.scrollTop = target.offsetTop;
          } else {
            const fraction = Math.min(Math.max(restoreFraction, 0), 1);
            const focusY = target.offsetTop + fraction * target.offsetHeight;
            scrollEl.scrollTop = focusY - scrollEl.clientHeight / 2;
          }
          if (restoreXFraction != null) {
            const xFraction = Math.min(Math.max(restoreXFraction, 0), 1);
            const focusX = target.offsetLeft + xFraction * target.offsetWidth;
            scrollEl.scrollLeft = focusX - scrollEl.clientWidth / 2;
          }
          requestAnimationFrame(() => {
            isScrollSyncRef.current = false;
          });
        }
      }

      // Phase 2: Render canvas content in the background
      for (const { page, canvas, viewport } of pageEntries) {
        if (cancelled()) return;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          // Scale the context to match devicePixelRatio
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
      }

      if (cancelled() || !scrollEl) return;

      // Scroll-based page + active-report detection. When pages from multiple
      // reports are appended into one container, find the page whose bounds
      // CONTAIN the viewport midpoint — and only update when there's a
      // confident match. In gaps (between pages, divider regions) we hold
      // the previous values steady so the toolbar can't snap to a wildly
      // different number (e.g. prev report's last page) just because the
      // midpoint briefly entered a divider while scrolling across reports.
      const handleScroll = () => {
        if (isScrollSyncRef.current) return;
        const scrollMid = scrollEl.scrollTop + scrollEl.clientHeight / 2;

        let containingPage: number | null = null;
        let containingReport: string | null = null;

        // Prefer staying in the currently-active report: if any of its
        // pages contain the midpoint, lock onto that. This avoids the
        // toolbar momentarily switching reports when the midpoint grazes
        // an adjacent report's page during a scroll.
        const lastActive = activeReportKeyRef.current;
        const pageEls = container.querySelectorAll("[data-page]");

        if (lastActive) {
          for (const el of Array.from(pageEls)) {
            const htmlEl = el as HTMLElement;
            if (htmlEl.getAttribute("data-report") !== lastActive) continue;
            const top = htmlEl.offsetTop;
            const bottom = top + htmlEl.offsetHeight;
            if (scrollMid >= top && scrollMid < bottom) {
              containingPage = parseInt(
                htmlEl.getAttribute("data-page") ?? "1",
                10
              );
              containingReport = lastActive;
              break;
            }
          }
        }

        // No page in the active report contains the midpoint — the user
        // has scrolled fully out of it. Look across all pages.
        if (containingPage === null) {
          for (const el of Array.from(pageEls)) {
            const htmlEl = el as HTMLElement;
            const top = htmlEl.offsetTop;
            const bottom = top + htmlEl.offsetHeight;
            if (scrollMid >= top && scrollMid < bottom) {
              containingPage = parseInt(
                htmlEl.getAttribute("data-page") ?? "1",
                10
              );
              containingReport = htmlEl.getAttribute("data-report");
              break;
            }
          }
        }

        // Still no match (midpoint sits in a gap or divider). Hold the
        // previous values — don't fall back to nearest-midpoint, which is
        // what caused the spurious "page 3 → page 5" jump when scrolling
        // up across a divider into the previous report.
        if (containingPage !== null && containingReport) {
          activeReportKeyRef.current = containingReport;
          const doc = pdfDocByReportRef.current.get(containingReport);
          if (doc) setTotalPdfPages(doc.numPages);
          if (lastActiveReportRef.current !== containingReport) {
            lastActiveReportRef.current = containingReport;
            const cb = onActiveReportChangeRef.current;
            if (cb) {
              const newReport = containingReport;
              requestAnimationFrame(() => {
                cb(newReport);
              });
            }
          }
          setCurrentPage(containingPage);
        }

        // Auto-load next: when the user scrolls into the bottom 25% of the
        // last appended report, kick off loading the next one.
        const nearBottomCb = onNearBottomRef.current;
        if (nearBottomCb) {
          const totalH = scrollEl.scrollHeight;
          const visibleBottom = scrollEl.scrollTop + scrollEl.clientHeight;
          if (totalH > 0 && visibleBottom / totalH > 0.75) {
            nearBottomCb();
          }
        }

        // Auto-load previous: when the user scrolls into the top 25% of the
        // first appended report, kick off loading the previous one.
        const nearTopCb = onNearTopRef.current;
        if (nearTopCb) {
          const totalH = scrollEl.scrollHeight;
          if (totalH > 0 && scrollEl.scrollTop / totalH < 0.25) {
            nearTopCb();
          }
        }
      };

      scrollEl.addEventListener("scroll", handleScroll, { passive: true });
      scrollCleanupRef.current = () =>
        scrollEl.removeEventListener("scroll", handleScroll);
      // Run once to seed currentPage / active report based on initial scroll.
      handleScroll();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ensureCanvasContainer]
  );

  // Continuous-scroll: append a single PDF doc's pages into the existing
  // container, prefixed by a polished section divider showing the report name.
  // Does NOT clear the container — it APPENDS, so the scroll position is
  // preserved and the user reads the next report seamlessly.
  const appendPdfDoc = useCallback(
    async (
      pdfDoc: any,
      zoom: number,
      reportKey: string,
      reportLabel: string,
      cancelled: () => boolean
    ) => {
      const container = ensureCanvasContainer();
      if (!container) return;

      const scrollEl = pdfScrollRef.current;
      const containerWidth = scrollEl ? scrollEl.clientWidth - 16 : 800;
      const dpr = (window.devicePixelRatio || 1) * 2;

      // Section divider — lets the reader know a new report is starting.
      const divider = document.createElement("div");
      divider.setAttribute("data-report-divider", reportKey);
      divider.style.cssText = `
        margin: 24px auto 16px;
        max-width: ${containerWidth}px;
        display: flex;
        align-items: center;
        gap: 12px;
        animation: wbrDividerFadeIn 0.4s ease-out;
      `;
      const line = (color: string) => {
        const el = document.createElement("div");
        el.style.cssText = `flex: 1; height: 1px; background: ${color};`;
        return el;
      };
      const label = document.createElement("div");
      label.style.cssText = `
        color: #FFD79A;
        font: 600 12px/1.4 -apple-system, system-ui, sans-serif;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        background: rgba(255,153,0,0.12);
        padding: 4px 12px;
        border-radius: 999px;
        border: 1px solid rgba(255,153,0,0.35);
        white-space: nowrap;
      `;
      label.textContent = `Next · ${reportLabel}`;
      divider.appendChild(line("rgba(255,255,255,0.18)"));
      divider.appendChild(label);
      divider.appendChild(line("rgba(255,255,255,0.18)"));
      container.appendChild(divider);

      // Phase 1: create wrappers + canvases sized correctly.
      const pageEntries: {
        page: any;
        canvas: HTMLCanvasElement;
        viewport: any;
      }[] = [];

      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        if (cancelled()) return;

        const defaultViewport = page.getViewport({ scale: 1 });
        const fitScale = containerWidth / defaultViewport.width;
        const scale = fitScale * (zoom / 100);
        const viewport = page.getViewport({ scale });

        const wrapper = document.createElement("div");
        wrapper.setAttribute("data-page", String(i));
        wrapper.setAttribute("data-report", reportKey);
        wrapper.style.marginBottom = "8px";
        wrapper.style.marginLeft = "auto";
        wrapper.style.marginRight = "auto";
        wrapper.style.background = "#fff";
        wrapper.style.boxShadow = "0 1px 4px rgba(0,0,0,0.15)";
        wrapper.style.width = "fit-content";

        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.style.display = "block";

        wrapper.appendChild(canvas);
        container.appendChild(wrapper);
        pageEntries.push({ page, canvas, viewport });
      }

      // Phase 2: render canvases.
      for (const { page, canvas, viewport } of pageEntries) {
        if (cancelled()) return;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
      }
    },
    [ensureCanvasContainer]
  );

  // Continuous-scroll (reverse): prepend a single PDF doc's pages to the top
  // of the existing container. Scroll position is preserved by measuring the
  // container's height before and after insertion and adjusting scrollTop by
  // the delta — the user keeps reading exactly where they were while the
  // newly-prepended report's content sits above them.
  const prependPdfDoc = useCallback(
    async (
      pdfDoc: any,
      zoom: number,
      reportKey: string,
      reportLabel: string,
      cancelled: () => boolean
    ) => {
      const container = ensureCanvasContainer();
      if (!container) return;

      const scrollEl = pdfScrollRef.current;
      const containerWidth = scrollEl ? scrollEl.clientWidth - 16 : 800;
      const dpr = (window.devicePixelRatio || 1) * 2;

      // Build all pages into a detached fragment first so we can measure the
      // total height before splicing it into the live DOM. This lets us
      // compensate scrollTop in one shot, avoiding a visible jump.
      const fragment = document.createDocumentFragment();

      // Section divider — same styling as the "Next" divider but labeled
      // "Previous" so the user knows what they just scrolled into.
      const divider = document.createElement("div");
      divider.setAttribute("data-report-divider", reportKey);
      divider.style.cssText = `
        margin: 24px auto 16px;
        max-width: ${containerWidth}px;
        display: flex;
        align-items: center;
        gap: 12px;
        animation: wbrDividerFadeIn 0.4s ease-out;
      `;
      const line = (color: string) => {
        const el = document.createElement("div");
        el.style.cssText = `flex: 1; height: 1px; background: ${color};`;
        return el;
      };
      const label = document.createElement("div");
      label.style.cssText = `
        color: #FFD79A;
        font: 600 12px/1.4 -apple-system, system-ui, sans-serif;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        background: rgba(255,153,0,0.12);
        padding: 4px 12px;
        border-radius: 999px;
        border: 1px solid rgba(255,153,0,0.35);
        white-space: nowrap;
      `;
      label.textContent = `Previous · ${reportLabel}`;
      divider.appendChild(line("rgba(255,255,255,0.18)"));
      divider.appendChild(label);
      divider.appendChild(line("rgba(255,255,255,0.18)"));
      fragment.appendChild(divider);

      // Phase 1: build wrappers + canvases sized correctly inside the fragment.
      const pageEntries: {
        page: any;
        canvas: HTMLCanvasElement;
        viewport: any;
      }[] = [];

      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        if (cancelled()) return;

        const defaultViewport = page.getViewport({ scale: 1 });
        const fitScale = containerWidth / defaultViewport.width;
        const scale = fitScale * (zoom / 100);
        const viewport = page.getViewport({ scale });

        const wrapper = document.createElement("div");
        wrapper.setAttribute("data-page", String(i));
        wrapper.setAttribute("data-report", reportKey);
        wrapper.style.marginBottom = "8px";
        wrapper.style.marginLeft = "auto";
        wrapper.style.marginRight = "auto";
        wrapper.style.background = "#fff";
        wrapper.style.boxShadow = "0 1px 4px rgba(0,0,0,0.15)";
        wrapper.style.width = "fit-content";

        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.style.display = "block";

        wrapper.appendChild(canvas);
        fragment.appendChild(wrapper);
        pageEntries.push({ page, canvas, viewport });
      }

      // Splice the fragment to the top of the container while compensating
      // scrollTop so the user's current view stays put. Guard the scroll
      // sync so handleScroll doesn't fire spurious near-top callbacks during
      // the layout shift.
      if (cancelled() || !scrollEl) return;
      const heightBefore = container.offsetHeight;
      isScrollSyncRef.current = true;
      container.insertBefore(fragment, container.firstChild);
      // Force layout so heightAfter reflects the new pages.
      const heightAfter = container.offsetHeight;
      const delta = heightAfter - heightBefore;
      if (delta > 0) {
        scrollEl.scrollTop += delta;
      }
      requestAnimationFrame(() => {
        isScrollSyncRef.current = false;
      });

      // Phase 2: render canvases.
      for (const { page, canvas, viewport } of pageEntries) {
        if (cancelled()) return;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          await page.render({ canvasContext: ctx, viewport }).promise;
        }
      }
    },
    [ensureCanvasContainer]
  );

  // Map of report filenames whose detail (URLs + PDF doc + callout HTML) is
  // currently being prefetched in the background, keyed to the in-flight
  // promise. Used to dedupe so the foreground loadNextReport/loadPrevReport
  // and the background prefetcher don't all fire for the same report — and
  // critically, so awaiters of a duplicate call actually WAIT for the
  // in-flight one to finish (rather than returning instantly to find the
  // cache still cold).
  const inFlightPrefetchRef = useRef<Map<string, Promise<void>>>(new Map());

  /**
   * Background prefetch for a single report. Populates all in-memory caches
   * (`pdfDocByReportRef`, `pdfUrlsByReportRef`, `calloutByReportRef`,
   * `highlightedByReportRef`) so a subsequent loadNextReport call hits warm
   * caches and renders almost instantly. NEVER touches the DOM. Idempotent;
   * concurrent calls for the same filename share one fetch — duplicate
   * callers `await` the same in-flight promise.
   */
  const prefetchReport = useCallback(
    (report: WBRReportMeta): Promise<void> => {
      const key = report.filename;
      if (!key) return Promise.resolve();
      if (pdfDocByReportRef.current.has(key)) return Promise.resolve();
      const existing = inFlightPrefetchRef.current.get(key);
      if (existing) return existing;

      const myToken = appendTokenRef.current;
      const stale = () => myToken !== appendTokenRef.current;

      const promise = (async () => {
        try {
          // 1) Fetch detail (URLs + callout HTML location).
          const result = await client.graphql({
            query: getWBRCallouts,
            variables: {
              input: {
                team_id: currentTeamId,
                team_name: currentTeamName,
                report_name: key,
                week: weekToParam(selectedWeek.year, selectedWeek.week),
              },
            },
          });
          if (stale()) return;
          const response = result.data.getWBRCallouts;
          const detail = response?.success ? response.reports?.[0] : null;
          if (!detail) return;

          const highlightedUrl = detail.source_pdf_url ?? null;
          const originalUrl = detail.original_pdf_url ?? null;
          const isHighlighted = detail.highlighted === true;
          highlightedByReportRef.current.set(key, isHighlighted);
          pdfUrlsByReportRef.current.set(key, {
            highlighted: highlightedUrl,
            original: originalUrl,
            isHighlighted,
          });

          const useHighlighted = cellRefsEnabled && isHighlighted;
          const pdfUrl = useHighlighted
            ? highlightedUrl
            : originalUrl ?? highlightedUrl;
          const calloutUrl = detail.callout_html_url ?? null;
          if (!pdfUrl) return;

          // 2) Load PDF doc + callout HTML in parallel.
          const pdfjsLib = await loadPdfJs();
          const [pdfDoc, calloutBundle] = await Promise.all([
            pdfjsLib.getDocument(pdfUrl).promise,
            (async () => {
              if (!calloutUrl)
                return { pages: [], styles: "", execSummaryHtml: "" };
              try {
                const resp = await fetch(calloutUrl);
                const html = await resp.text();
                return parseCalloutPages(html);
              } catch {
                return { pages: [], styles: "", execSummaryHtml: "" };
              }
            })(),
          ]);
          if (stale()) return;

          pdfDocByReportRef.current.set(key, pdfDoc);
          calloutByReportRef.current.set(key, {
            pages: calloutBundle.pages,
            styles: calloutBundle.styles,
            execSummaryHtml: calloutBundle.execSummaryHtml,
            calloutHtmlUrl: calloutUrl,
          });
        } catch (err) {
          // Background prefetch failures are non-fatal — loadNextReport will
          // retry the same fetch path on demand.
          console.debug("Prefetch failed for", key, err);
        } finally {
          inFlightPrefetchRef.current.delete(key);
        }
      })();

      inFlightPrefetchRef.current.set(key, promise);
      return promise;
    },
    // client.graphql is stable per render but ESLint can't see that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTeamId, currentTeamName, selectedWeek, cellRefsEnabled]
  );

  // Continuous-scroll: fetch + parse + append the next report after the last
  // one already rendered. Idempotent; bails out if already in flight, no
  // remaining reports, or context has changed (week / team / merged-view).
  const loadNextReport = useCallback(async () => {
    if (appendingNextRef.current) return;
    if (isMergedView) return;
    if (!reportList || reportList.length === 0) return;
    if (appendedReports.length === 0) return;

    const last = appendedReports[appendedReports.length - 1];
    const lastIndex = reportList.findIndex((r) => r.filename === last.filename);
    if (lastIndex < 0 || lastIndex >= reportList.length - 1) return;
    const next = reportList[lastIndex + 1];
    // Defensive: if the next report is somehow already in the list, bail.
    // Shouldn't happen given lastIndex points to the tail, but it's cheap.
    if (appendedReports.find((r) => r.filename === next.filename)) return;

    // Flip the synchronous guard BEFORE any await so a rapidly-fired second
    // scroll callback in the same tick is rejected. React state still
    // updates for the loading badge UI.
    appendingNextRef.current = true;
    setAppendingNext(true);
    const myToken = appendTokenRef.current;
    const cancelled = () => myToken !== appendTokenRef.current;

    try {
      // Ensure the next report's PDF doc + callout bundle are in cache.
      // If a background prefetch already populated them, this returns
      // immediately. Otherwise it does the full fetch path itself.
      await prefetchReport(next);
      if (cancelled()) return;

      const pdfDoc = pdfDocByReportRef.current.get(next.filename);
      if (!pdfDoc) {
        throw new Error("Next report failed to load");
      }

      // Append the PDF pages to the existing scroll container.
      await appendPdfDoc(
        pdfDoc,
        pdfZoomRef.current,
        next.filename,
        next.showing_name,
        cancelled
      );
      if (cancelled()) return;

      setAppendedReports((prev) => {
        // Defensive: only append if not already there (race between renders).
        if (prev.find((r) => r.filename === next.filename)) return prev;
        return [...prev, next];
      });
    } catch (err) {
      console.error("Failed to auto-load next report:", err);
    } finally {
      appendingNextRef.current = false;
      if (!cancelled()) setAppendingNext(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appendedReports, reportList, isMergedView, appendPdfDoc, prefetchReport]);

  // Continuous-scroll (reverse): fetch + parse + prepend the previous report
  // before the first one already rendered. Mirror of loadNextReport. Idempotent;
  // bails out if already in flight, head is already report[0], or context has
  // changed (week / team / merged-view).
  const loadPrevReport = useCallback(async () => {
    if (appendingPrevRef.current) return;
    if (isMergedView) return;
    if (!reportList || reportList.length === 0) return;
    if (appendedReports.length === 0) return;

    const first = appendedReports[0];
    const firstIndex = reportList.findIndex(
      (r) => r.filename === first.filename
    );
    if (firstIndex <= 0) return;
    const prev = reportList[firstIndex - 1];
    if (appendedReports.find((r) => r.filename === prev.filename)) return;

    appendingPrevRef.current = true;
    setAppendingPrev(true);
    const myToken = appendTokenRef.current;
    const cancelled = () => myToken !== appendTokenRef.current;

    try {
      await prefetchReport(prev);
      if (cancelled()) return;

      const pdfDoc = pdfDocByReportRef.current.get(prev.filename);
      if (!pdfDoc) {
        throw new Error("Previous report failed to load");
      }

      // Prepend pages above the existing content; the helper compensates
      // scrollTop so the user's view stays anchored to what they were reading.
      await prependPdfDoc(
        pdfDoc,
        pdfZoomRef.current,
        prev.filename,
        prev.showing_name,
        cancelled
      );
      if (cancelled()) return;

      setAppendedReports((cur) => {
        if (cur.find((r) => r.filename === prev.filename)) return cur;
        return [prev, ...cur];
      });
    } catch (err) {
      console.error("Failed to auto-load previous report:", err);
    } finally {
      appendingPrevRef.current = false;
      if (!cancelled()) setAppendingPrev(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appendedReports,
    reportList,
    isMergedView,
    prependPdfDoc,
    prefetchReport,
  ]);

  // Active-report change: when the user scrolls into a different report's
  // pages, swap the right-pane data (callouts, exec summary) to match.
  const handleActiveReportChange = useCallback(
    (key: string) => {
      const nextReport = appendedReports.find((r) => r.filename === key);
      if (!nextReport) return;
      const sameReport = selectedReport?.filename === key;
      if (!sameReport) {
        setSelectedReport(nextReport);
        const cached = calloutByReportRef.current.get(key);
        if (cached) {
          setCalloutPages(cached.pages);
          setCalloutStyles(cached.styles);
          setExecSummaryHtml(cached.execSummaryHtml);
          setCalloutHtmlUrl(cached.calloutHtmlUrl);
        }
      }
      // Update `totalPdfPages` and the cell-refs toggle visibility flag on
      // every call — even when the active report hasn't actually changed.
      // The pdfDoc / highlight state can be stale (e.g., right after a
      // cell-refs toggle re-renders the same report with a different doc),
      // and the early-exit on sameReport would otherwise leave the toolbar
      // showing the previous report's page count until the next scroll.
      const doc = pdfDocByReportRef.current.get(key);
      if (doc) setTotalPdfPages(doc.numPages);
      const hl = highlightedByReportRef.current.get(key);
      if (typeof hl === "boolean") setCurrentReportHasHighlights(hl);
    },
    [appendedReports, selectedReport]
  );

  // Wire the scroll-handler refs to the latest callbacks every render so the
  // listener (which closes over stable refs) sees fresh state.
  useEffect(() => {
    onActiveReportChangeRef.current = handleActiveReportChange;
    onNearBottomRef.current = loadNextReport;
    onNearTopRef.current = loadPrevReport;
  }, [handleActiveReportChange, loadNextReport, loadPrevReport]);

  // Background prefetch — keep one report ahead of the tail warm in cache so
  // the next user-triggered loadNextReport renders instantly. Re-runs when:
  //   • a new report finishes appending (so we look one further ahead), or
  //   • the cell-refs toggle flips (so we cache the URL matching the new
  //     toggle state — the activePdfUrl effect clears the doc cache on
  //     toggle so this re-prefetches with the right URL).
  useEffect(() => {
    if (isMergedView) return;
    if (!reportList || reportList.length === 0) return;
    if (appendedReports.length === 0) return;
    const tail = appendedReports[appendedReports.length - 1];
    const tailIndex = reportList.findIndex((r) => r.filename === tail.filename);
    if (tailIndex < 0 || tailIndex >= reportList.length - 1) return;
    const nextAhead = reportList[tailIndex + 1];
    if (pdfDocByReportRef.current.has(nextAhead.filename)) return;
    // Fire and forget. prefetchReport handles its own dedupe + cancellation.
    void prefetchReport(nextAhead);
  }, [
    appendedReports,
    reportList,
    isMergedView,
    cellRefsEnabled,
    prefetchReport,
  ]);

  // Background prefetch (reverse) — keep one report behind the head warm in
  // cache so the next user-triggered loadPrevReport renders instantly.
  // Symmetric to the tail prefetcher above.
  useEffect(() => {
    if (isMergedView) return;
    if (!reportList || reportList.length === 0) return;
    if (appendedReports.length === 0) return;
    const head = appendedReports[0];
    const headIndex = reportList.findIndex((r) => r.filename === head.filename);
    if (headIndex <= 0) return;
    const prevBehind = reportList[headIndex - 1];
    if (pdfDocByReportRef.current.has(prevBehind.filename)) return;
    void prefetchReport(prevBehind);
  }, [
    appendedReports,
    reportList,
    isMergedView,
    cellRefsEnabled,
    prefetchReport,
  ]);

  // Load PDF document AND render canvases when activePdfUrl changes.
  // Waits for the scroll container DOM to be available before rendering.
  useEffect(() => {
    if (!activePdfUrl) return;
    let cancelled = false;

    // Distinguish two scenarios:
    //   a) Cell-refs toggle reload — `pendingScrollAfterToggleRef` was set by
    //      `toggleCellRefs`. The user shouldn't see ANY visual disruption
    //      (no flicker to head's first page, no toolbar flashing 1/0).
    //   b) New-report or fresh load — pendingScrollAfterToggleRef is null.
    //      Clear and show a loading state.
    const isToggleReload = !!pendingScrollAfterToggleRef.current;

    pdfDocRef.current = null;
    if (isToggleReload && pdfScrollRef.current) {
      // Hide the scroll element so the user can't see the rebuild.
      // The container is cleared after this — but visibility:hidden means
      // the empty/in-progress state never reaches the screen.
      pdfScrollRef.current.style.visibility = "hidden";
    }
    if (pdfScrollRef.current) {
      pdfScrollRef.current.innerHTML = "";
    }
    pdfCanvasContainerRef.current = null;
    if (!isToggleReload) {
      // Keep `totalPdfPages` at the active report's current value during a
      // toggle reload — resetting to 0 makes the toolbar flash "1 / 0".
      setTotalPdfPages(0);
      setPdfLoading(true);
    }
    scrollCleanupRef.current?.();
    scrollCleanupRef.current = null;

    // The active PDF URL changed (e.g., cell-refs toggle, new report).
    // We keep the appended-reports list intact so the user retains the
    // continuous-scroll session; each report is re-rendered below using its
    // matching highlighted/original URL from the per-report URL cache.
    appendTokenRef.current += 1;
    const myToken = appendTokenRef.current;
    const isStale = () => myToken !== appendTokenRef.current;
    // Stable cancel checker hoisted out of the loop body below — referencing
    // the mutable `cancelled` directly inside a function declared in a loop
    // trips the no-loop-func ESLint rule.
    const isCancelled = () => cancelled || isStale();
    pdfDocByReportRef.current.clear();
    inFlightPrefetchRef.current.clear();
    lastActiveReportRef.current = null;
    activeReportKeyRef.current = null;

    const headReport = appendedReports[0] ?? selectedReport;
    const headKey = headReport?.filename;
    const reportsToReload = appendedReports.length > 0 ? appendedReports : [];

    (async () => {
      // Hold the scroll-sync guard from before render through post-append
      // scroll restoration. Without this, handleScroll() seeded by
      // renderPdfPages fires onActiveReportChange(headKey) — which mutates
      // selectedReport and triggers a full reload of head's PDF (the user
      // sees the link/filename swap to the first report).
      const pending = pendingScrollAfterToggleRef.current;
      const willRestoreScroll = !!pending;
      if (willRestoreScroll) {
        isScrollSyncRef.current = true;
      }
      const releaseSyncGuard = () => {
        if (willRestoreScroll) {
          requestAnimationFrame(() => {
            isScrollSyncRef.current = false;
          });
        }
      };

      try {
        const pdfjsLib = await loadPdfJs();
        // Prefer the per-report URL cache for the head when available — the
        // head may be a prepended report whose URLs aren't reflected in
        // `activePdfUrl` (which is derived from `selectedReport`'s URL state).
        // Falls back to `activePdfUrl` for the originally-selected head.
        let headPdfUrl: string | null = activePdfUrl;
        if (headKey) {
          const cachedHead = pdfUrlsByReportRef.current.get(headKey);
          if (cachedHead) {
            const useHighlighted = cellRefsEnabled && cachedHead.isHighlighted;
            const fromCache = useHighlighted
              ? cachedHead.highlighted
              : cachedHead.original ?? cachedHead.highlighted;
            if (fromCache) headPdfUrl = fromCache;
          }
        }
        const loadingTask = pdfjsLib.getDocument(headPdfUrl);
        const pdfDoc = await loadingTask.promise;
        if (cancelled || isStale()) {
          releaseSyncGuard();
          return;
        }

        pdfDocRef.current = pdfDoc;
        if (headKey) pdfDocByReportRef.current.set(headKey, pdfDoc);
        // Don't update `totalPdfPages` here on a toggle reload — it would
        // briefly show head's page count even when the active report is a
        // scrolled-into B/C/… The pending-restore block below sets it from
        // the active report's doc once scroll is restored.
        if (!isToggleReload) {
          setTotalPdfPages(pdfDoc.numPages);
        }

        // Wait for the scroll container to be mounted (it may not exist yet
        // if detailLoading just flipped to false in the same render batch).
        let retries = 0;
        while (!pdfScrollRef.current && retries < 20) {
          await new Promise((r) => setTimeout(r, 50));
          retries++;
          if (cancelled) {
            releaseSyncGuard();
            return;
          }
        }
        if (!pdfScrollRef.current || cancelled) {
          releaseSyncGuard();
          return;
        }

        await renderPdfPages(
          pdfDoc,
          pdfZoomRef.current,
          isCancelled,
          undefined,
          undefined,
          undefined,
          headKey
        );

        // Re-append every additional report (B/C/…) that was already in the
        // continuous-scroll session, picking the URL that matches the current
        // cell-refs toggle from the per-report URL cache. Falls back to a
        // network fetch only if the cache miss (unexpected).
        for (let i = 1; i < reportsToReload.length; i++) {
          if (isCancelled()) {
            releaseSyncGuard();
            return;
          }
          const r = reportsToReload[i];
          const cachedUrls = pdfUrlsByReportRef.current.get(r.filename);
          let pdfUrl: string | null = null;
          if (cachedUrls) {
            const useHighlighted = cellRefsEnabled && cachedUrls.isHighlighted;
            pdfUrl = useHighlighted
              ? cachedUrls.highlighted
              : cachedUrls.original ?? cachedUrls.highlighted;
          }
          if (!pdfUrl) continue;
          try {
            const subDoc = await pdfjsLib.getDocument(pdfUrl).promise;
            if (isCancelled()) {
              releaseSyncGuard();
              return;
            }
            pdfDocByReportRef.current.set(r.filename, subDoc);
            await appendPdfDoc(
              subDoc,
              pdfZoomRef.current,
              r.filename,
              r.showing_name,
              isCancelled
            );
          } catch (e) {
            console.warn(
              "Failed to re-append report after toggle:",
              r.filename,
              e
            );
          }
        }

        // Re-warm the prefetch cache for the report immediately after the
        // tail, since the toggle reload cleared the doc cache. Without this
        // the user's first post-toggle scroll-down would have to wait for
        // the full fetch path again.
        if (!isCancelled() && reportsToReload.length > 0) {
          const tail = reportsToReload[reportsToReload.length - 1];
          const tailIdx = reportList.findIndex(
            (r) => r.filename === tail.filename
          );
          if (tailIdx >= 0 && tailIdx < reportList.length - 1) {
            void prefetchReport(reportList[tailIdx + 1]);
          }
        }

        // Restore the user's scroll position to the report+page they were on
        // before the toggle. Done after every report has been appended so
        // the layout heights are stable.
        if (pending && !isCancelled()) {
          pendingScrollAfterToggleRef.current = null;
          const scrollEl = pdfScrollRef.current;
          const container = pdfCanvasContainerRef.current;
          if (scrollEl && container) {
            const selector = `[data-page="${
              pending.page
            }"][data-report="${CSS.escape(pending.activeKey)}"]`;
            const target = container.querySelector(
              selector
            ) as HTMLElement | null;
            if (target) {
              // Use direct scrollTop instead of scrollIntoView — the latter
              // can cascade up to ancestor scroll containers and is async.
              scrollEl.scrollTop = target.offsetTop;
              setCurrentPage(pending.page);
              activeReportKeyRef.current = pending.activeKey;
              lastActiveReportRef.current = pending.activeKey;
              // The toolbar's denominator (`totalPdfPages`) tracks the
              // active report's pdfDoc.numPages. handleActiveReportChange
              // would normally update it via setTotalPdfPages — but it
              // early-returns when `selectedReport.filename === key`, which
              // is true here whenever the user toggled while viewing an
              // appended report that had already become the selected one
              // via scroll. Without this direct sync, the toolbar shows
              // head's page count until the user scrolls and handleScroll
              // re-fires. Update it here so "5 / 7" appears immediately
              // after the toggle instead of after the next scroll event.
              const activeDoc = pdfDocByReportRef.current.get(
                pending.activeKey
              );
              if (activeDoc) setTotalPdfPages(activeDoc.numPages);
              // Sync toolbar title + URL share-state to the report the user
              // was actually viewing (which may be an appended B/C/… report,
              // not the head). handleActiveReportChange also swaps callouts
              // / exec-summary panes to match.
              const cb = onActiveReportChangeRef.current;
              if (cb) cb(pending.activeKey);
            }
          }
        }
      } catch (err) {
        console.error("Error loading PDF:", err);
        if (!cancelled) setError("Failed to load PDF");
      } finally {
        if (!cancelled) setPdfLoading(false);
        // Reveal the scroll element. Done after scroll restoration so the
        // user never sees the in-progress rebuild flashing past — the
        // PDF appears in-place at the correct page in one frame.
        if (isToggleReload && pdfScrollRef.current) {
          // requestAnimationFrame lets the browser commit the scroll
          // restoration BEFORE we unhide, so the user can't catch the
          // pre-restore frame flash.
          requestAnimationFrame(() => {
            if (pdfScrollRef.current) {
              pdfScrollRef.current.style.visibility = "";
            }
          });
        }
        releaseSyncGuard();
      }
    })();

    return () => {
      cancelled = true;
      scrollCleanupRef.current?.();
      scrollCleanupRef.current = null;
    };
    // We intentionally also depend on cellRefsEnabled so flipping the toggle
    // always re-runs the reload+re-append loop even when the head report's
    // own URL doesn't change (e.g., head has no highlights but appended
    // reports B/C do). Without this, the per-report URL pick that matches
    // the new toggle state would be silently skipped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePdfUrl, cellRefsEnabled]);

  // Re-render canvases when zoom changes (without re-fetching the document).
  useEffect(() => {
    const pdfDoc = pdfDocRef.current;
    const scrollEl = pdfScrollRef.current;
    if (!pdfDoc || !scrollEl) return;
    let cancelled = false;

    // Surface a spinner while the canvases reflow — the scroll container is
    // hidden during the re-render below, so without this the PDF blanks out
    // and looks stuck.
    setPdfZooming(true);

    scrollCleanupRef.current?.();
    scrollCleanupRef.current = null;

    const pageToRestore = currentPage;

    // Capture the fractional position within the current page that sits at
    // the viewport's center (both axes), so zooming keeps that focus point
    // steady instead of jumping to the page top-left.
    // In continuous-scroll mode multiple reports share the same page numbers,
    // so scope the lookup to the active report — otherwise the captured
    // fraction (and post-render scroll) latches onto the head report.
    const headReport = appendedReports[0] ?? selectedReport;
    const headKey = headReport?.filename;
    const activeKey = activeReportKeyRef.current ?? headKey ?? null;

    let yFraction: number | undefined;
    let xFraction: number | undefined;
    const currentPageEl = (
      activeKey
        ? pdfCanvasContainerRef.current?.querySelector(
            `[data-page="${pageToRestore}"][data-report="${CSS.escape(
              activeKey
            )}"]`
          )
        : pdfCanvasContainerRef.current?.querySelector(
            `[data-page="${pageToRestore}"]`
          )
    ) as HTMLElement | null;
    if (currentPageEl && currentPageEl.offsetHeight > 0) {
      const viewportCenterY = scrollEl.scrollTop + scrollEl.clientHeight / 2;
      yFraction =
        (viewportCenterY - currentPageEl.offsetTop) /
        currentPageEl.offsetHeight;
    }
    if (currentPageEl && currentPageEl.offsetWidth > 0) {
      const viewportCenterX = scrollEl.scrollLeft + scrollEl.clientWidth / 2;
      xFraction =
        (viewportCenterX - currentPageEl.offsetLeft) /
        currentPageEl.offsetWidth;
    }

    const isCancelled = () => cancelled;
    (async () => {
      // For single-report (or when active report IS head), restore scroll
      // during render. Otherwise we render head first, append the rest,
      // then scroll to the active report's page below.
      const restoreInline = !activeKey || activeKey === headKey;

      // When the active report isn't head, hold the scroll-sync guard from
      // before render through the post-append scroll restoration. Without
      // this, the handleScroll() seed call at the end of renderPdfPages
      // fires onActiveReportChange(headKey) — which mutates selectedReport,
      // recomputes activePdfUrl, and triggers a full reload of head's PDF
      // (visually swapping the link/filename to the first report).
      if (!restoreInline) {
        isScrollSyncRef.current = true;
      }

      // Hide the scroll element's contents during the reflow so the user
      // doesn't see head's first page flash by while we tear down, re-render,
      // and reposition. We still need scrollEl itself visible so its layout
      // (clientWidth/Height) stays accurate for the scroll restoration math.
      const prevVisibility = scrollEl.style.visibility;
      scrollEl.style.visibility = "hidden";
      const reveal = () => {
        scrollEl.style.visibility = prevVisibility;
        // Only clear the spinner if this run is still current — a newer zoom
        // run (which set pdfZooming=true again) owns it otherwise.
        if (!cancelled) setPdfZooming(false);
      };

      await renderPdfPages(
        pdfDoc,
        pdfZoom,
        isCancelled,
        restoreInline ? pageToRestore : undefined,
        restoreInline ? yFraction : undefined,
        restoreInline ? xFraction : undefined,
        headKey
      );
      if (cancelled) {
        if (!restoreInline) isScrollSyncRef.current = false;
        reveal();
        return;
      }
      // Re-append every additional report that was already appended,
      // so zoom changes preserve the continuous-scroll layout.
      for (let i = 1; i < appendedReports.length; i++) {
        const r = appendedReports[i];
        const doc = pdfDocByReportRef.current.get(r.filename);
        if (!doc) continue;
        await appendPdfDoc(
          doc,
          pdfZoom,
          r.filename,
          r.showing_name,
          isCancelled
        );
      }
      if (cancelled || restoreInline || !activeKey) {
        if (!restoreInline) {
          requestAnimationFrame(() => {
            isScrollSyncRef.current = false;
          });
        }
        // Reveal on next frame so the (just-applied) inline-restore scroll
        // is painted before the user sees anything.
        requestAnimationFrame(reveal);
        return;
      }
      // Active report isn't head — scroll to its page now that all reports
      // have been appended into the container.
      const target = pdfCanvasContainerRef.current?.querySelector(
        `[data-page="${pageToRestore}"][data-report="${CSS.escape(activeKey)}"]`
      ) as HTMLElement | null;
      if (target) {
        // isScrollSyncRef is already true (set above).
        if (yFraction == null) {
          scrollEl.scrollTop = target.offsetTop;
        } else {
          const yf = Math.min(Math.max(yFraction, 0), 1);
          const focusY = target.offsetTop + yf * target.offsetHeight;
          scrollEl.scrollTop = focusY - scrollEl.clientHeight / 2;
        }
        if (xFraction != null) {
          const xf = Math.min(Math.max(xFraction, 0), 1);
          const focusX = target.offsetLeft + xf * target.offsetWidth;
          scrollEl.scrollLeft = focusX - scrollEl.clientWidth / 2;
        }
        activeReportKeyRef.current = activeKey;
        lastActiveReportRef.current = activeKey;
      }
      requestAnimationFrame(() => {
        isScrollSyncRef.current = false;
        reveal();
      });
    })();

    return () => {
      cancelled = true;
      scrollCleanupRef.current?.();
      scrollCleanupRef.current = null;
    };
    // currentPage intentionally excluded — only re-render on zoom change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfZoom]);

  // Re-render PDF canvases when the viewer becomes visible again.
  // Navigating to another submenu sets isOpen=false which returns null and
  // destroys the DOM (including pdfScrollRef). When isOpen flips back to true
  // the activePdfUrl hasn't changed so the loading effect won't re-fire.
  // This effect detects that scenario and re-renders from the cached pdfDoc.
  useEffect(() => {
    if (!isOpen) return;
    const pdfDoc = pdfDocRef.current;
    if (!pdfDoc || !activePdfUrl) return;

    // The canvas container was destroyed when the component returned null.
    // Reset it so renderPdfPages creates a fresh one.
    pdfCanvasContainerRef.current = null;

    let cancelled = false;

    // Wait for the scroll container ref to be mounted after re-render,
    // retrying up to 20 times (same strategy as the initial PDF load).
    const timer = setTimeout(async () => {
      if (cancelled) return;

      let retries = 0;
      while (!pdfScrollRef.current && retries < 20) {
        await new Promise((r) => setTimeout(r, 50));
        retries++;
        if (cancelled) return;
      }
      if (!pdfScrollRef.current || cancelled) return;

      scrollCleanupRef.current?.();
      scrollCleanupRef.current = null;
      await renderPdfPages(
        pdfDoc,
        pdfZoomRef.current,
        () => cancelled,
        currentPage
      );
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Only trigger when isOpen changes; other deps are refs or stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Scroll callout pane to top when the synced page changes.
  // In merged view: content is already filtered to current page, just reset scroll.
  // In report detail view: scroll to top (only one page's callouts shown).
  useEffect(() => {
    calloutPaneRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, isMergedView]);

  // Draggable split divider handlers
  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.max(20, Math.min(80, pct)));
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  // Navigate to a specific page — scrolls the PDF container to that page
  const goToPage = useCallback(
    (page: number) => {
      const maxPage = totalPdfPages || calloutPages.length || 1;
      const clamped = Math.max(1, Math.min(page, maxPage));
      setCurrentPage(clamped);

      // Scroll the PDF container to the target page. In continuous-scroll
      // mode, restrict the lookup to the active report's pages so the page
      // number isn't ambiguous (each report restarts at 1).
      const container = pdfCanvasContainerRef.current;
      if (!container) return;
      const activeKey =
        activeReportKeyRef.current ?? selectedReport?.filename ?? null;
      const selector = activeKey
        ? `[data-page="${clamped}"][data-report="${CSS.escape(activeKey)}"]`
        : `[data-page="${clamped}"]`;
      const target = (container.querySelector(selector) ||
        container.querySelector(
          `[data-page="${clamped}"]`
        )) as HTMLElement | null;
      if (target) {
        isScrollSyncRef.current = true;
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        // Re-enable observer after scroll settles
        setTimeout(() => {
          isScrollSyncRef.current = false;
        }, 600);
      }
    },
    [totalPdfPages, calloutPages.length, selectedReport]
  );

  // Auto-seek to the page supplied by ?page= URL param once the PDF is ready.
  // Runs once per pending value; cleared so subsequent scrolls don't get hijacked.
  useEffect(() => {
    if (pendingPage === null) return;
    if (totalPdfPages === 0) return; // wait for PDF to load
    // Small delay to let the PDF canvases finish measuring before scrolling.
    const t = setTimeout(() => {
      goToPage(pendingPage);
      setPendingPage(null);
    }, 150);
    return () => clearTimeout(t);
  }, [pendingPage, totalPdfPages, goToPage]);

  // ── Share link: copy current URL to clipboard ────────────────────────
  const [shareSnackbar, setShareSnackbar] = useState<string | null>(null);
  const handleShareLink = useCallback(async () => {
    const shareUrl = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for older browsers / non-secure contexts
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setShareSnackbar("Link copied to clipboard");
    } catch (err) {
      console.error("Failed to copy share link:", err);
      setShareSnackbar("Unable to copy link — select the URL manually");
    }
  }, []);

  // Week picker helpers
  const handleWeekSelect = useCallback((year: number, week: number) => {
    const monday = getISOWeekMonday(year, week);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() - 1);
    const saturday = new Date(monday);
    saturday.setDate(monday.getDate() + 5);
    setSelectedWeek({ year, week, startDate: sunday, endDate: saturday });
    setWeekPickerAnchor(null);
    // (URL sync handled by the central writeShareUrl effect — it will
    //  clear the ?report/?page/?merged params because state is reset below)
    // Clear current report selection when week changes
    setSelectedReport(null);
    // Clear the report list so the auto-select-first-report effect doesn't
    // fire with the previous week's reports while the new week's list is
    // still being fetched. Without this, the effect picks the old week's
    // first report and tries to load it for the new week, surfacing as
    // "Source PDF not available" with a stale ?report= URL param.
    setReportList([]);
    setSourcePdfUrl(null);
    setCalloutHtmlUrl(null);
    setOriginalPdfUrl(null);
    setHasHighlightedPdf(false);
    setCurrentReportHasHighlights(false);
    setCalloutPages([]);
    setCalloutStyles("");
    setExecSummaryHtml("");
    setCurrentPage(1);
    setTotalPdfPages(0);
    setRightTab(0);
    // Clear folder summary state for the new week
    setFolderSummaryUrl(null);
    setFolderSummaryHtml(null);
    setFolderSummaryError(null);
    // Clear merged view state when week changes
    setIsMergedView(false);
    setMergedPdfUrl(null);
    setMergedHighlightedPdfUrl(null);
    setHighlightMap(null);
    setPageOffsetMap(null);
    setSkippedFiles(null);
    setMergedError(null);
    setBuildingHighlightedMerge(false);
    // Reset continuous-scroll state — appended reports from the previous
    // week would otherwise survive the week change and block the auto-
    // select-first-report effect for the new week.
    setAppendedReports([]);
    setAppendingNext(false);
    setAppendingPrev(false);
    appendingNextRef.current = false;
    appendingPrevRef.current = false;
    calloutByReportRef.current.clear();
    pdfDocByReportRef.current.clear();
    highlightedByReportRef.current.clear();
    pdfUrlsByReportRef.current.clear();
    inFlightPrefetchRef.current.clear();
    pdfDocRef.current = null;
    if (pdfCanvasContainerRef.current) {
      pdfCanvasContainerRef.current.innerHTML = "";
    }
  }, []);

  /** Navigate to the previous or next ISO week. direction: -1 = prev, 1 = next. */
  const navigateWeek = useCallback(
    (direction: -1 | 1) => {
      const { year, week } = selectedWeek;
      let targetYear = year;
      let targetWeek = week + direction;

      if (targetWeek < 1) {
        // Going backward from week 1 → last week of previous year
        targetYear = year - 1;
        targetWeek = getWeeksInYear(targetYear);
      } else if (targetWeek > getWeeksInYear(year)) {
        // Going forward past last week → week 1 of next year
        targetYear = year + 1;
        targetWeek = 1;
      }

      handleWeekSelect(targetYear, targetWeek);
    },
    [selectedWeek, handleWeekSelect]
  );

  const currentWBR = useMemo(() => getCurrentWBRWeek(), []);
  const isCurrentWeek =
    selectedWeek.year === currentWBR.year &&
    selectedWeek.week === currentWBR.week;

  // Derive active view from existing state (not stored separately)
  const activeView: "summary" | "merged" | "report" = isMergedView
    ? "merged"
    : selectedReport
    ? "report"
    : "summary";

  // ── WBR chatbox context: tell the assistant what the user is viewing so
  // answers ground to the on-screen week/report/page instead of the latest
  // week. Memoized; the chatbox reads these at send time (Req: view context).
  const chatboxViewContext = useMemo<WbrViewContext>(() => {
    const ctx: WbrViewContext = {
      week: weekToParam(selectedWeek.year, selectedWeek.week),
      weekLabel: `WK${selectedWeek.week} · ${fmtShort(
        selectedWeek.startDate
      )} – ${fmtShort(selectedWeek.endDate)}, ${selectedWeek.year}`,
      view: activeView,
    };
    if (activeView === "report" && selectedReport) {
      ctx.reportName = selectedReport.filename;
      if (currentPage > 0) ctx.pageNum = currentPage;
    }
    return ctx;
  }, [selectedWeek, activeView, selectedReport, currentPage]);

  // Bounded projection of the on-screen callouts (never raw HTML). Capped so
  // the request payload stays small; deeper questions fall through to get_wbr.
  const chatboxViewCallouts = useMemo<WbrViewCallout[]>(() => {
    // Bounded, plain-text projection. `why` is trimmed to a single short line
    // (callouts are in display/card order, NOT ranked by magnitude — the block
    // labels them accordingly so the model doesn't treat #1 as "the biggest").
    const oneLine = (t: string | null | undefined): string | undefined => {
      if (!t) return undefined;
      const collapsed = t.replace(/\s+/g, " ").trim();
      if (!collapsed) return undefined;
      return collapsed.length > 200 ? collapsed.slice(0, 197) + "…" : collapsed;
    };
    return reportCallouts.slice(0, 5).map((c) => ({
      title: c.title,
      status: c.status,
      why: oneLine(c.why_text),
    }));
  }, [reportCallouts]);

  // Build segments array for the View Switcher — only the PDF selector remains.
  // Summary and Merged segments removed in favor of continuous-scroll PDF flow.
  const segments = useMemo(() => {
    return [
      {
        key: "report",
        label: selectedReport?.showing_name || "Select Report",
        icon: <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />,
      },
    ];
  }, [selectedReport]);

  // Measure the active segment's position and update the sliding pill style
  // Re-measure whenever the active view changes OR the report label text changes
  // (segments.length alone won't catch label changes when switching reports)
  const activeSegmentLabel = segments.find((s) => s.key === activeView)?.label;
  useEffect(() => {
    const el = segmentRefs.current.get(activeView);
    if (el) {
      setPillStyle({ left: el.offsetLeft, width: el.offsetWidth });
    }
  }, [activeView, segments.length, activeSegmentLabel]);

  // Crossfade transition: fade out on view change, then fade back in
  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 120);
    return () => clearTimeout(timer);
  }, [activeView, selectedReport?.filename]);

  const totalWeeksInPickerYear = useMemo(
    () => getWeeksInYear(pickerYear),
    [pickerYear]
  );

  const currentCalloutPage = calloutPages.find(
    (p) => p.pageNumber === currentPage
  );

  // Count total callout cards across all pages (for the tab badge).
  // Sourced from the unified hook so AI and manual cards both contribute, and
  // we don't double-count by also parsing the AI HTML.
  const totalCalloutCount = useMemo(
    () => reportCallouts.length,
    [reportCallouts]
  );

  // Impact filter state: Set of active impact levels for multi-select
  const [impactFilter, setImpactFilter] = useState<string>(() => {
    // Seed from ?impact= URL param — default "all" if missing or invalid
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("impact");
    return raw &&
      ["all", "large", "medium", "small", "large+medium"].includes(raw)
      ? raw
      : "all";
  });
  // Multi-select impact levels for mobile: which individual levels are shown
  const [mobileImpactLevels, setMobileImpactLevels] = useState<Set<string>>(
    () => new Set(["large", "medium", "small"])
  );
  const calloutContentRef = useRef<HTMLDivElement>(null);

  // ── Central URL-sync effect ────────────────────────────────────────────
  // Keeps the URL in lock-step with the viewer's state so that copying the
  // browser URL at any moment produces a stable, reproducible share link.
  // Skips runs while a "pending" URL param hasn't been applied yet, so
  // incoming deep-links aren't wiped out before their state lands.
  // Only runs while the viewer is the active view — non-VESL teams don't
  // even see the WBR Callouts menu item, so isOpen=false there keeps their
  // URL clean. VESL teams land on this viewer by default, so the URL params
  // (?week=…&team=…&view=wbr-callouts) make the page reproducible/shareable.
  useEffect(() => {
    if (!isOpen) return;
    if (pendingReport || pendingMerged || pendingPage !== null) return;
    // Strip any "VESL - " prefix so the share link uses the short team name
    // that MainPage's ?team= matcher accepts. MainPage already re-prepends
    // "vesl - " when matching, so full names still round-trip too.
    const teamForUrl = currentTeamName
      ? currentTeamName.replace(/^VESL - /i, "")
      : undefined;
    writeShareUrl({
      week: weekToParam(selectedWeek.year, selectedWeek.week),
      report:
        activeView === "report" && selectedReport
          ? selectedReport.filename
          : undefined,
      merged: activeView === "merged",
      page: currentPage,
      tab: rightTab === 1 ? "callouts" : "exec",
      cellRefs: cellRefsEnabled,
      impact: impactFilter,
      team: teamForUrl,
    });
  }, [
    isOpen,
    selectedWeek,
    activeView,
    selectedReport,
    currentPage,
    rightTab,
    cellRefsEnabled,
    impactFilter,
    pendingReport,
    pendingMerged,
    pendingPage,
    currentTeamName,
  ]);

  // Apply impact filter to the rendered callout cards in the DOM
  // Supports both legacy string filter (desktop) and multi-select Set (mobile)
  const applyImpactFilter = useCallback((filter: string) => {
    setImpactFilter(filter);
    const container = calloutContentRef.current;
    if (!container) return;

    container.querySelectorAll<HTMLElement>(".callout-card").forEach((card) => {
      if (card.classList.contains("no-callout-card")) {
        card.style.display = filter === "all" ? "" : "none";
        return;
      }
      const badge = card.querySelector(".impact-badge");
      const isLarge = badge?.classList.contains("impact-large") ?? false;
      const isMedium = badge?.classList.contains("impact-medium") ?? false;
      let show = false;
      if (filter === "all") show = true;
      else if (filter === "large") show = isLarge;
      else if (filter === "large+medium") show = isLarge || isMedium;
      card.style.display = show ? "" : "none";
    });

    // Hide page section titles that have no visible cards
    container
      .querySelectorAll<HTMLElement>(".page-section-title")
      .forEach((title) => {
        let sibling = title.nextElementSibling as HTMLElement | null;
        let hasVisible = false;
        while (sibling && !sibling.classList.contains("page-section-title")) {
          if (
            sibling.classList.contains("callout-card") &&
            sibling.style.display !== "none"
          ) {
            hasVisible = true;
            break;
          }
          sibling = sibling.nextElementSibling as HTMLElement | null;
        }
        title.style.display = hasVisible ? "" : "none";
      });
  }, []);

  // Mobile multi-select: toggle an individual impact level and apply to DOM
  const toggleMobileImpactLevel = useCallback((level: string) => {
    setMobileImpactLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) {
        // Don't allow deselecting all — keep at least one
        if (next.size > 1) next.delete(level);
      } else {
        next.add(level);
      }
      // Apply to DOM
      const container = calloutContentRef.current;
      if (container) {
        container
          .querySelectorAll<HTMLElement>(".callout-card")
          .forEach((card) => {
            if (card.classList.contains("no-callout-card")) {
              card.style.display = next.size === 3 ? "" : "none";
              return;
            }
            const badge = card.querySelector(".impact-badge");
            const isLarge = badge?.classList.contains("impact-large") ?? false;
            const isMedium =
              badge?.classList.contains("impact-medium") ?? false;
            const isSmall = badge?.classList.contains("impact-small") ?? false;
            const show =
              (isLarge && next.has("large")) ||
              (isMedium && next.has("medium")) ||
              (isSmall && next.has("small")) ||
              (!isLarge && !isMedium && !isSmall && next.has("small")); // no badge = small
            card.style.display = show ? "" : "none";
          });
        // Hide empty page section titles
        container
          .querySelectorAll<HTMLElement>(".page-section-title")
          .forEach((title) => {
            let sibling = title.nextElementSibling as HTMLElement | null;
            let hasVisible = false;
            while (
              sibling &&
              !sibling.classList.contains("page-section-title")
            ) {
              if (
                sibling.classList.contains("callout-card") &&
                sibling.style.display !== "none"
              ) {
                hasVisible = true;
                break;
              }
              sibling = sibling.nextElementSibling as HTMLElement | null;
            }
            title.style.display = hasVisible ? "" : "none";
          });
      }
      return next;
    });
  }, []);

  // Download the callout HTML file from the pre-signed S3 URL
  const handleDownloadHtml = useCallback(async () => {
    if (!calloutHtmlUrl) return;
    try {
      // Fetch within the app context (pre-signed URL works here)
      const resp = await fetch(calloutHtmlUrl);
      let html = await resp.text();
      const baseName =
        selectedReport?.filename?.replace(/\.[^/.]+$/, "") ?? "wbr_callouts";

      // The downloaded HTML already contains AI cards — only inject manual ones.
      const manualOnlyCallouts = reportCallouts.filter(
        (c) => c.source === "manual"
      );

      // Inject manual callout cards into the correct page sections
      if (manualOnlyCallouts.length > 0) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Build a helper to generate manual callout card HTML
        const toCardHtml = (c: Callout) => {
          const isPositive = c.status === "positive";
          const statusClass = isPositive ? "positive" : "watch";
          const statusLabel = isPositive ? "&#9650; Positive" : "&#9660; Watch";
          const whySection = c.why_text
            ? `<details class="why-details" open>
                 <summary>Why Called Out</summary>
                 <div class="why-body-text">${c.why_text.replace(
                   /\n/g,
                   "<br>"
                 )}</div>
               </details>`
            : "";
          const trendBadge = c.trend
            ? `<span class="trend-badge">${c.trend}</span>`
            : "";
          const creatorInfo = c.creator_name
            ? `Created by ${c.creator_name}`
            : "";
          return `<div class="callout-card" style="border-left: 4px solid ${
            isPositive ? "#2E7D32" : "#B85450"
          };">
            <div class="card-top-row">
              <span class="status-badge ${statusClass}">${statusLabel}</span>
              ${trendBadge}
              <span class="source-badge source-manual" style="display:inline-flex;align-items:center;font-size:9px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;padding:2px 6px;border-radius:4px;background:#FFF3E0;color:#E65100;">MANUAL</span>
            </div>
            <div class="headline">${c.title}</div>
            ${whySection}
            <div style="font-size:11px;color:#9E9E9E;font-style:italic;margin-top:8px;">${creatorInfo}</div>
          </div>`;
        };

        // Group manual callouts by page_num
        const byPage = new Map<number, Callout[]>();
        manualOnlyCallouts.forEach((c) => {
          const arr = byPage.get(c.page_num) || [];
          arr.push(c);
          byPage.set(c.page_num, arr);
        });

        // Find page section titles and insert manual cards after the last AI card in each section
        const pageTitles = doc.querySelectorAll(".page-section-title");
        pageTitles.forEach((titleEl) => {
          const titleText = titleEl.textContent || "";
          const match = titleText.match(/Page\s+(\d+)/i);
          if (!match) return;
          const pageNum = parseInt(match[1], 10);
          const pageCallouts = byPage.get(pageNum);
          if (!pageCallouts || pageCallouts.length === 0) return;

          // Find the last element in this page section (before next page-section-title or divider)
          let lastInSection: Element = titleEl;
          let sibling = titleEl.nextElementSibling;
          while (sibling) {
            if (sibling.classList.contains("page-section-title")) break;
            lastInSection = sibling;
            sibling = sibling.nextElementSibling;
          }

          // Insert manual callout cards after the last element in this section
          const fragment = doc.createDocumentFragment();
          pageCallouts.forEach((c) => {
            const wrapper = doc.createElement("div");
            wrapper.innerHTML = toCardHtml(c);
            while (wrapper.firstChild) {
              fragment.appendChild(wrapper.firstChild);
            }
          });
          lastInSection.parentNode?.insertBefore(
            fragment,
            lastInSection.nextSibling
          );

          // Remove from map so we can handle orphans
          byPage.delete(pageNum);
        });

        // Any remaining callouts (pages not found in AI HTML) — append before </body>
        byPage.forEach((callouts, pageNum) => {
          const sectionHtml =
            `<div class="page-section-title">Page ${pageNum} &mdash; Manual Callouts</div>\n` +
            callouts.map((c) => toCardHtml(c)).join("\n") +
            `\n<hr class="divider">`;
          const wrapper = doc.createElement("div");
          wrapper.innerHTML = sectionHtml;
          while (wrapper.firstChild) {
            doc.body.appendChild(wrapper.firstChild);
          }
        });

        // Serialize back to string
        html = doc.documentElement.outerHTML;
      }

      // Use data URI approach — works on both desktop and mobile/AEA browser
      // without needing blob URLs or external navigation
      const dataUri =
        "data:text/html;charset=utf-8," + encodeURIComponent(html);
      const a = document.createElement("a");
      a.href = dataUri;
      a.download = `${baseName}_callouts.html`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to download callout HTML:", err);
    }
  }, [calloutHtmlUrl, selectedReport, reportCallouts]);

  // Re-apply filter whenever the page changes or all pages are rendered
  useEffect(() => {
    if (isMobile) {
      // On mobile, re-apply multi-select filter
      if (mobileImpactLevels.size < 3) {
        const timer = setTimeout(() => {
          // Trigger a no-op toggle to re-apply current levels to new DOM
          setMobileImpactLevels((prev) => new Set(prev));
          const container = calloutContentRef.current;
          if (!container) return;
          container
            .querySelectorAll<HTMLElement>(".callout-card")
            .forEach((card) => {
              if (card.classList.contains("no-callout-card")) {
                card.style.display =
                  mobileImpactLevels.size === 3 ? "" : "none";
                return;
              }
              const badge = card.querySelector(".impact-badge");
              const isLarge =
                badge?.classList.contains("impact-large") ?? false;
              const isMedium =
                badge?.classList.contains("impact-medium") ?? false;
              const isSmall =
                badge?.classList.contains("impact-small") ?? false;
              const show =
                (isLarge && mobileImpactLevels.has("large")) ||
                (isMedium && mobileImpactLevels.has("medium")) ||
                (isSmall && mobileImpactLevels.has("small")) ||
                (!isLarge &&
                  !isMedium &&
                  !isSmall &&
                  mobileImpactLevels.has("small"));
              card.style.display = show ? "" : "none";
            });
          container
            .querySelectorAll<HTMLElement>(".page-section-title")
            .forEach((title) => {
              let sibling = title.nextElementSibling as HTMLElement | null;
              let hasVisible = false;
              while (
                sibling &&
                !sibling.classList.contains("page-section-title")
              ) {
                if (
                  sibling.classList.contains("callout-card") &&
                  sibling.style.display !== "none"
                ) {
                  hasVisible = true;
                  break;
                }
                sibling = sibling.nextElementSibling as HTMLElement | null;
              }
              title.style.display = hasVisible ? "" : "none";
            });
        }, 50);
        return () => clearTimeout(timer);
      }
    } else if (impactFilter !== "all") {
      const timer = setTimeout(() => applyImpactFilter(impactFilter), 50);
      return () => clearTimeout(timer);
    }
  }, [
    currentPage,
    currentCalloutPage,
    calloutPages,
    isMobile,
    impactFilter,
    mobileImpactLevels,
    applyImpactFilter,
  ]);

  // Auto-expand all "Why Called Out" details sections when callout content renders
  // On mobile, keep them collapsed to save space
  useEffect(() => {
    if (isMobile) return; // Don't auto-expand on mobile
    const timer = setTimeout(() => {
      const container = calloutContentRef.current;
      if (!container) return;
      container
        .querySelectorAll<HTMLDetailsElement>(".why-details")
        .forEach((el) => {
          el.open = true;
        });
    }, 60);
    return () => clearTimeout(timer);
  }, [currentPage, currentCalloutPage, calloutPages, isMobile, rightTab]);

  if (!isOpen) return null;

  return (
    <>
      <Box
        component="main"
        sx={{
          ml: `${menuWidth}px`,
          width: `calc(100vw - ${menuWidth}px)`,
          transition: "margin-left 0.2s ease, width 0.2s ease",
          height: isMobile ? "100dvh" : "100vh", // dvh accounts for mobile browser chrome
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Shimmer + continuous-scroll divider keyframes */}
        <style>{`
          @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
          @keyframes wbrDividerFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
        {/* ── Tier 1 Header: Navy bar with title + week stepper ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: isMobile ? 1 : 3,
            height: isMobile ? 38 : 44,
            bgcolor: NAVY,
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {/* Left: Title */}
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: isMobile ? 16 : undefined }}
          >
            FINN Brief
            {currentTeamName
              ? ` - ${currentTeamName.replace(/^VESL\s*-\s*/, "")}`
              : ""}
          </Typography>

          {/* Right: Week Stepper — placeholder, task 2.2 will implement full stepper */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => navigateWeek(-1)}
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
              }}
              aria-label="Previous week"
            >
              <ChevronLeftIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            <Typography
              sx={{
                cursor: "pointer",
                fontWeight: 600,
                fontSize: isMobile ? 13 : 14,
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={(e) => {
                setPickerYear(selectedWeek.year);
                setWeekPickerAnchor(e.currentTarget);
              }}
            >
              {isMobile
                ? `WK${selectedWeek.week}`
                : `WK${selectedWeek.week} · ${fmtShort(
                    selectedWeek.startDate
                  )} – ${fmtShort(selectedWeek.endDate)}, ${selectedWeek.year}`}
            </Typography>
            {isCurrentWeek && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: ACCENT_ORANGE,
                  flexShrink: 0,
                }}
              />
            )}
            <IconButton
              size="small"
              onClick={() => navigateWeek(1)}
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
              }}
              aria-label="Next week"
            >
              <ChevronRightIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Tier 2 Header: Frosted glass bar with view switcher + actions ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: isMobile ? 1 : 3,
            height: isMobile ? 36 : 40,
            bgcolor: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            flexShrink: 0,
          }}
        >
          {/* Left: View Switcher — pill-shaped segmented control */}
          <Box
            sx={{
              bgcolor: "rgba(0,0,0,0.06)",
              borderRadius: "24px",
              padding: "3px",
              position: "relative",
              display: "inline-flex",
            }}
          >
            {/* Sliding pill indicator */}
            <Box
              sx={{
                position: "absolute",
                top: 3,
                height: "calc(100% - 6px)",
                bgcolor: ACCENT_ORANGE,
                borderRadius: "20px",
                transition: "transform 200ms ease, width 200ms ease",
                transform: `translateX(${pillStyle.left}px)`,
                width: pillStyle.width,
              }}
            />
            {segments.map((seg) => {
              const isActive = seg.key === activeView;
              return (
                <ButtonBase
                  key={seg.key}
                  ref={(el: HTMLElement | null) => {
                    if (el) {
                      segmentRefs.current.set(seg.key, el);
                    } else {
                      segmentRefs.current.delete(seg.key);
                    }
                  }}
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    if (seg.key === "summary") {
                      // Same cleanup as existing "back to summary" / week-change logic
                      setSelectedReport(null);
                      setIsMergedView(false);
                      setSourcePdfUrl(null);
                      setCalloutHtmlUrl(null);
                      setOriginalPdfUrl(null);
                      setHasHighlightedPdf(false);
                      setCalloutPages([]);
                      setCalloutStyles("");
                      setExecSummaryHtml("");
                      setCurrentPage(1);
                      setTotalPdfPages(0);
                      setRightTab(0);
                      setMergedPdfUrl(null);
                      setMergedHighlightedPdfUrl(null);
                      setHighlightMap(null);
                      setPageOffsetMap(null);
                      setSkippedFiles(null);
                      setMergedError(null);
                      pdfDocRef.current = null;
                      if (pdfCanvasContainerRef.current) {
                        pdfCanvasContainerRef.current.innerHTML = "";
                      }
                    } else if (seg.key === "merged") {
                      if (!isMergedView) {
                        handleEnterMergedView();
                      }
                      // If already in merged view, no-op
                    } else if (seg.key === "report") {
                      // Open report picker menu (or reopen for quick-switch)
                      setReportMenuAnchor(e.currentTarget);
                    }
                  }}
                  sx={{
                    borderRadius: "20px",
                    px: 2,
                    py: 0.5,
                    position: "relative",
                    zIndex: 1,
                    color: isActive ? NAVY : "rgba(0,0,0,0.6)",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 13,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    transition: "color 150ms ease",
                  }}
                >
                  {isMobile ? (
                    <>
                      {seg.icon}
                      {seg.key === "report" && (
                        <span style={{ fontSize: 10, marginLeft: 2 }}>▾</span>
                      )}
                    </>
                  ) : (
                    <>
                      {seg.label}
                      {seg.key === "report" && (
                        <span style={{ fontSize: 10, marginLeft: 2 }}>▾</span>
                      )}
                    </>
                  )}
                </ButtonBase>
              );
            })}
          </Box>

          {/* Right: Action icons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Admin-only: upload WBR PDF(s) into wbr-publish for this team/week.
                Placed before the share link per UX. Server re-verifies admin
                (team / group / FINN) — see create_wbr_publish_upload_url Lambda. */}
            <WbrPublishUploadButton
              teamId={currentTeamId}
              weekEndDate={selectedWeek.endDate}
              canUpload={canUploadWbrPdf}
              isCurrentWeek={isCurrentWeek}
              onUploaded={() => fetchReportList()}
              fetchReadyFilenames={fetchReadyFilenames}
            />
            <Tooltip title="Copy shareable link to this view">
              <IconButton
                size="small"
                onClick={handleShareLink}
                aria-label="Copy shareable link"
              >
                <IosShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {calloutHtmlUrl && (
              <Tooltip title="Download callout HTML">
                <IconButton size="small" onClick={handleDownloadHtml}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Week Picker Popover — kept as-is, triggered by week label click */}
        <Popover
          open={Boolean(weekPickerAnchor)}
          anchorEl={weekPickerAnchor}
          onClose={() => setWeekPickerAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: { sx: { borderRadius: 2, mt: 0.5, overflow: "hidden" } },
          }}
        >
          <Paper sx={{ width: 340, p: 0 }} elevation={0}>
            {/* Year header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                bgcolor: NAVY,
                color: "#fff",
              }}
            >
              <IconButton
                size="small"
                onClick={() => setPickerYear((y) => y - 1)}
                sx={{ color: "#fff" }}
                aria-label="Previous year"
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, letterSpacing: 1 }}
              >
                {pickerYear}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPickerYear((y) => y + 1)}
                sx={{ color: "#fff" }}
                aria-label="Next year"
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            <Divider />

            {/* Week grid */}
            <Box sx={{ p: 1.5, maxHeight: 320, overflowY: "auto" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  gap: 0.5,
                }}
              >
                {Array.from({ length: totalWeeksInPickerYear }, (_, i) => {
                  const wk = i + 1;
                  const isSelected =
                    pickerYear === selectedWeek.year &&
                    wk === selectedWeek.week;
                  const isCurrent =
                    pickerYear === currentWBR.year && wk === currentWBR.week;
                  // Compute date range for tooltip
                  const mon = getISOWeekMonday(pickerYear, wk);
                  const sun = new Date(mon);
                  sun.setDate(mon.getDate() - 1);
                  const sat = new Date(mon);
                  sat.setDate(mon.getDate() + 5);
                  const rangeStr = `${fmtShort(sun)} – ${fmtShort(sat)}`;

                  return (
                    <Tooltip key={wk} title={rangeStr} arrow placement="top">
                      <Button
                        size="small"
                        variant={isSelected ? "contained" : "text"}
                        onClick={() => handleWeekSelect(pickerYear, wk)}
                        sx={{
                          minWidth: 0,
                          px: 0.5,
                          py: 0.75,
                          fontSize: 12,
                          fontWeight: isSelected ? 700 : 500,
                          borderRadius: 1.5,
                          bgcolor: isSelected ? ACCENT_ORANGE : "transparent",
                          color: isSelected ? NAVY : "text.primary",
                          border:
                            isCurrent && !isSelected
                              ? `2px solid ${ACCENT_ORANGE}`
                              : "2px solid transparent",
                          "&:hover": {
                            bgcolor: isSelected ? "#e68a00" : "action.hover",
                          },
                        }}
                      >
                        {wk}
                      </Button>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>

            {/* Footer with date range */}
            <Divider />
            <Box sx={{ px: 2, py: 1, bgcolor: "#f5f5f5" }}>
              <Typography variant="caption" color="text.secondary">
                WK{selectedWeek.week}: {fmtShort(selectedWeek.startDate)} –{" "}
                {fmtShort(selectedWeek.endDate)}, {selectedWeek.year}
                {" · "}S3 folder: {fmtISO(selectedWeek.endDate)}
              </Typography>
            </Box>
          </Paper>
        </Popover>

        {/* Report Picker Menu — kept as-is, will be triggered by View Switcher in task 3 */}
        <Menu
          anchorEl={reportMenuAnchor}
          open={Boolean(reportMenuAnchor)}
          onClose={() => setReportMenuAnchor(null)}
          slotProps={{ paper: { sx: { maxHeight: 400, minWidth: 280 } } }}
        >
          {loading ? (
            <MenuItem disabled>
              <CircularProgress size={18} sx={{ mr: 1 }} />
              <ListItemText primary="Loading..." />
            </MenuItem>
          ) : reportList.length === 0 ? (
            <MenuItem disabled>
              <ListItemText primary="No reports for this week" />
            </MenuItem>
          ) : (
            reportList.map((report) => (
              <MenuItem
                key={report.filename}
                selected={selectedReport?.filename === report.filename}
                onClick={() => handleReportSelect(report)}
              >
                <ListItemText
                  primary={report.showing_name}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </MenuItem>
            ))
          )}
        </Menu>

        {/* Error Banner */}
        {error && (
          <Alert
            severity="error"
            sx={{ mx: 2, mt: 1, flexShrink: 0 }}
            action={
              <Button color="inherit" size="small" onClick={fetchReportList}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Crossfade wrapper around content area */}
        <Box
          sx={{
            opacity: isTransitioning ? 0 : 1,
            transition: "opacity 150ms ease-in-out",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Body: Two-Column Split */}
          {isMergedView ? (
            mergedLoading ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  gap: 1,
                  p: 1,
                  overflow: "hidden",
                }}
              >
                {isMobile ? (
                  <Box sx={{ ...skeletonSx, flex: 1, height: "100%" }} />
                ) : (
                  <>
                    <Box
                      sx={{
                        ...skeletonSx,
                        width: `${splitPercent}%`,
                        height: "100%",
                      }}
                    />
                    <Box
                      sx={{
                        ...skeletonSx,
                        width: `${100 - splitPercent}%`,
                        height: "100%",
                      }}
                    />
                  </>
                )}
              </Box>
            ) : mergedError ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography color="error">{mergedError}</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleEnterMergedView}
                  >
                    Retry
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setIsMergedView(false);
                      setMergedPdfUrl(null);
                      setMergedHighlightedPdfUrl(null);
                      setHighlightMap(null);
                      setPageOffsetMap(null);
                      setSkippedFiles(null);
                      setMergedError(null);
                    }}
                  >
                    Return to Summary
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                ref={splitContainerRef}
                sx={{ flex: 1, display: "flex", overflow: "hidden" }}
              >
                {/* Left Pane: Merged PDF rendered via pdf.js */}
                {!isMobile && (
                  <Box
                    sx={{
                      width: `${splitPercent}%`,
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      borderRight: "1px solid #e0e0e0",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    {/* Merged PDF Toolbar */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 0.5,
                        bgcolor: "#f5f5f5",
                        borderBottom: "1px solid #e0e0e0",
                        minHeight: 40,
                        flexShrink: 0,
                      }}
                    >
                      <MergeTypeIcon
                        fontSize="small"
                        sx={{ color: ACCENT_ORANGE }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Merged WBR — WK{selectedWeek.week}
                      </Typography>
                      {pdfLoading && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 10,
                            fontWeight: 700,
                            color: "text.secondary",
                            bgcolor: "rgba(0,0,0,0.04)",
                            border: "1px solid rgba(0,0,0,0.12)",
                            borderRadius: 999,
                            px: 0.75,
                            py: "1px",
                            ml: 0.5,
                            letterSpacing: 0.4,
                            textTransform: "uppercase",
                          }}
                        >
                          <CircularProgress
                            size={9}
                            thickness={6}
                            sx={{ color: ACCENT_ORANGE, flexShrink: 0 }}
                          />
                          Loading…
                        </Box>
                      )}
                      <Box sx={{ flex: 1 }} />

                      {/* Page navigation */}
                      {totalPdfPages > 0 && (
                        <>
                          <Tooltip title="Previous page">
                            <span>
                              <IconButton
                                size="small"
                                disabled={currentPage <= 1}
                                onClick={() => goToPage(currentPage - 1)}
                                aria-label="Previous page"
                              >
                                <NavigateBeforeIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Typography
                            variant="caption"
                            sx={{
                              mx: 0.75,
                              fontVariantNumeric: "tabular-nums",
                              color: "text.secondary",
                              userSelect: "none",
                              minWidth: 48,
                              textAlign: "center",
                            }}
                            aria-label={`Page ${currentPage} of ${totalPdfPages}`}
                          >
                            {currentPage} / {totalPdfPages}
                          </Typography>
                          <Tooltip title="Next page">
                            <span>
                              <IconButton
                                size="small"
                                disabled={currentPage >= totalPdfPages}
                                onClick={() => goToPage(currentPage + 1)}
                                aria-label="Next page"
                              >
                                <NavigateNextIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", mr: 1 }}
                      >
                        {pdfZoom}%
                      </Typography>
                      <Tooltip title="Zoom out">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPdfZoom((z) => Math.max(z - 15, 50))
                            }
                            disabled={pdfZoom <= 50}
                            aria-label="Zoom out PDF"
                          >
                            <ZoomOutIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Zoom in">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPdfZoom((z) => Math.min(z + 15, 250))
                            }
                            disabled={pdfZoom >= 250}
                            aria-label="Zoom in PDF"
                          >
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {mergedHasHighlights && (
                        <Tooltip
                          title={
                            buildingHighlightedMerge
                              ? "Building highlighted PDF…"
                              : cellRefsEnabled
                              ? "Hide cell highlights"
                              : "Show cell highlights"
                          }
                        >
                          <IconButton
                            size="small"
                            disabled={buildingHighlightedMerge}
                            onClick={() => {
                              const next = !cellRefsEnabled;
                              toggleCellRefs(next);
                              // Trigger on-demand build if toggling ON and no URL yet
                              if (next && !mergedHighlightedPdfUrl) {
                                buildHighlightedMerge();
                              }
                            }}
                            sx={{
                              color: cellRefsEnabled
                                ? "#FF9900"
                                : "text.secondary",
                              bgcolor: cellRefsEnabled
                                ? "rgba(255,153,0,0.1)"
                                : "transparent",
                              borderRadius: 1,
                              ml: 1,
                            }}
                            aria-label={
                              cellRefsEnabled
                                ? "Hide cell highlights"
                                : "Show cell highlights"
                            }
                          >
                            {buildingHighlightedMerge ? (
                              <CircularProgress
                                size={16}
                                sx={{ color: ACCENT_ORANGE }}
                              />
                            ) : (
                              <GridOnIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Fullscreen">
                        <IconButton
                          size="small"
                          onClick={() =>
                            pdfScrollRef.current?.requestFullscreen?.()
                          }
                          aria-label="Fullscreen PDF"
                        >
                          <FullscreenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Merged PDF Content — rendered via pdf.js canvases */}
                    <Box
                      sx={{
                        flex: 1,
                        position: "relative",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        ref={pdfScrollRef}
                        sx={{
                          flex: 1,
                          overflow: "auto",
                          bgcolor: "#525659",
                        }}
                      />
                      {pdfZooming && (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 20,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1.5,
                            bgcolor: "rgba(82, 86, 89, 0.55)",
                            pointerEvents: "none",
                          }}
                        >
                          <CircularProgress
                            size={36}
                            sx={{ color: ACCENT_ORANGE }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: "#fff", fontWeight: 600 }}
                          >
                            Zooming to {pdfZoom}%…
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {!activeMergedPdfUrl && !pdfLoading && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 10,
                          color: "text.secondary",
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "#ccc" }}>
                          Merged PDF not available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Draggable Divider — hidden on mobile */}
                {!isMobile && (
                  <Box
                    onMouseDown={handleDividerMouseDown}
                    sx={{
                      width: 6,
                      flexShrink: 0,
                      cursor: "col-resize",
                      bgcolor: "#e0e0e0",
                      "&:hover": { bgcolor: ACCENT_ORANGE },
                      transition: "background-color 0.15s",
                    }}
                  />
                )}

                {/* Right Pane: Merged Callout Panel */}
                <Box
                  sx={{
                    width: isMobile ? "100%" : `${100 - splitPercent}%`,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  {/* Impact filter toolbar for merged view */}
                  {mergedCalloutData && mergedCalloutData.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        px: 2,
                        py: 0.5,
                        bgcolor: "#fff",
                        borderBottom: "1px solid #E2E8F0",
                        flexShrink: 0,
                        minHeight: 32,
                      }}
                    >
                      <FilterListIcon
                        sx={{ fontSize: 14, color: "#94A3B8", mr: 0.5 }}
                      />
                      {[
                        { key: "all", label: "All" },
                        { key: "large", label: "Large" },
                        { key: "large+medium", label: "Large + Medium" },
                      ].map(({ key, label }) => (
                        <Box
                          key={key}
                          component="button"
                          onClick={() => {
                            setImpactFilter(key);
                            // Apply filter to merged callout DOM
                            setTimeout(() => {
                              const container = calloutPaneRef.current;
                              if (!container) return;
                              container
                                .querySelectorAll<HTMLElement>(".callout-card")
                                .forEach((card) => {
                                  if (
                                    card.classList.contains("no-callout-card")
                                  ) {
                                    card.style.display =
                                      key === "all" ? "" : "none";
                                    return;
                                  }
                                  const badge =
                                    card.querySelector(".impact-badge") ||
                                    card.querySelector(".size-badge");
                                  const isLarge =
                                    badge?.classList.contains("impact-large") ||
                                    badge?.classList.contains("size-large") ||
                                    false;
                                  const isMedium =
                                    badge?.classList.contains(
                                      "impact-medium"
                                    ) ||
                                    badge?.classList.contains("size-medium") ||
                                    false;
                                  let show = false;
                                  if (key === "all") show = true;
                                  else if (key === "large") show = isLarge;
                                  else if (key === "large+medium")
                                    show = isLarge || isMedium;
                                  card.style.display = show ? "" : "none";
                                });
                            }, 30);
                          }}
                          sx={{
                            border: "none",
                            outline: "none",
                            cursor: "pointer",
                            fontSize: 10.5,
                            fontWeight: 600,
                            fontFamily: "inherit",
                            px: 0.75,
                            py: "2px",
                            borderRadius: 0.75,
                            whiteSpace: "nowrap",
                            lineHeight: 1.4,
                            transition: "all 0.15s ease",
                            ...(impactFilter === key
                              ? { bgcolor: "#1B3A6B", color: "#fff" }
                              : {
                                  bgcolor: "transparent",
                                  color: "#64748B",
                                  "&:hover": {
                                    bgcolor: "#F1F5F9",
                                    color: "#1B3A6B",
                                  },
                                }),
                          }}
                        >
                          {label}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Scrollable Callout Content */}
                  <Box
                    ref={calloutPaneRef}
                    sx={{
                      flex: 1,
                      overflow: "auto",
                      bgcolor: "#F5F5F5",
                    }}
                  >
                    {/* Skipped files warning */}
                    {skippedFiles && skippedFiles.length > 0 && (
                      <Alert severity="warning" sx={{ m: 1, mb: 0 }}>
                        Some files could not be included:{" "}
                        {skippedFiles.join(", ")}
                      </Alert>
                    )}

                    {/* Merged callout sections — filtered to current PDF page */}
                    {mergedCalloutData && mergedCalloutData.length > 0 ? (
                      (() => {
                        const sectionsForPage = mergedCalloutData.filter(
                          (s) => s.page_number === currentPage
                        );
                        return sectionsForPage.length > 0 ? (
                          <Box sx={{ p: 1.5 }}>
                            {sectionsForPage.map((section, idx) => {
                              return (
                                <React.Fragment
                                  key={`${section.source_report}-${section.page_number}-${idx}`}
                                >
                                  {/* Page Section Header */}
                                  <Box
                                    data-page-number={section.page_number}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      px: 1.5,
                                      py: 0.75,
                                      mb: 0.5,
                                      bgcolor: "#E8ECF4",
                                      borderRadius: 1,
                                      borderLeft: `3px solid ${ACCENT_ORANGE}`,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 700,
                                        color: NAVY,
                                        fontSize: 11,
                                        textTransform: "uppercase",
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      {section.title ||
                                        `Page ${section.page_number}`}
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <Chip
                                      label={section.source_report}
                                      size="small"
                                      sx={{
                                        bgcolor: ACCENT_ORANGE,
                                        color: NAVY,
                                        fontWeight: 600,
                                        fontSize: 9,
                                        height: 18,
                                        "& .MuiChip-label": { px: 0.75 },
                                      }}
                                    />
                                  </Box>

                                  {/* Callout Cards (unified AI + manual) */}
                                  {section.callouts.length > 0 ? (
                                    section.callouts.map((c) => (
                                      <CalloutCard
                                        key={`merged-${c.id}`}
                                        callout={c}
                                        isStale={isStaleCallout(
                                          c,
                                          aiGenTimestamp
                                        )}
                                        canEdit={canEditCallout(c)}
                                        canDelete={canEditCallout(c)}
                                        onEdit={handleEditCallout}
                                        onDelete={(id) =>
                                          setDeleteConfirmId(id)
                                        }
                                      />
                                    ))
                                  ) : (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        px: 1.5,
                                        py: 1,
                                        color: "text.secondary",
                                      }}
                                    >
                                      No callouts for this page
                                    </Typography>
                                  )}
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() =>
                                      handleAddCallout(section.page_number)
                                    }
                                    sx={{
                                      mt: 0.5,
                                      mb: 1,
                                      textTransform: "none",
                                    }}
                                  >
                                    Add Callout
                                  </Button>
                                </React.Fragment>
                              );
                            })}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "100%",
                              color: "text.secondary",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2">
                              No callouts for page {currentPage}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#aaa" }}
                            >
                              Navigate to a page with callouts using the PDF
                              viewer
                            </Typography>
                          </Box>
                        );
                      })()
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          color: "text.secondary",
                        }}
                      >
                        <Typography variant="body2">
                          No callout data available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )
          ) : !selectedReport ? (
            folderSummaryLoading ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    ...skeletonSx,
                    height: 400,
                    maxWidth: 820,
                    width: "100%",
                    mx: "auto",
                    mt: 3,
                  }}
                />
              </Box>
            ) : folderSummaryHtml ? (
              <Box sx={{ flex: 1, overflow: "auto", bgcolor: "#F5F5F5" }}>
                {/* Summary HTML content — extract styles from the full HTML doc,
                  then strip everything before Consolidated Executive Summary */}
                <Box sx={{ p: isMobile ? 2 : 3 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        // Extract all <style>...</style> blocks from the original HTML
                        const styleRegex = /<style[\s\S]*?<\/style>/gi;
                        const styleMatches =
                          folderSummaryHtml.match(styleRegex) || [];
                        const originalStyles = styleMatches.join("\n");

                        const idx = folderSummaryHtml.indexOf(
                          '<div class="exec-section-header">'
                        );
                        const bodyContent =
                          idx >= 0
                            ? folderSummaryHtml.substring(idx)
                            : folderSummaryHtml;

                        return (
                          originalStyles +
                          FOLDER_SUMMARY_OVERRIDES +
                          (isMobile ? MOBILE_FOLDER_SUMMARY_OVERRIDES : "") +
                          bodyContent
                        );
                      })(),
                    }}
                  />
                </Box>
              </Box>
            ) : folderSummaryError ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography color="error" sx={{ mb: 1 }}>
                  {folderSummaryError}
                </Typography>
                <Button onClick={() => setFolderSummaryUrl((url) => url)}>
                  Retry
                </Button>
              </Box>
            ) : (
              /* Existing placeholder — shown when folder_summary_url is null */
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "text.secondary",
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 64, mb: 2, opacity: 0.4 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Select a report
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Week {selectedWeek.week} · {fmtShort(selectedWeek.startDate)}{" "}
                  – {fmtShort(selectedWeek.endDate)}, {selectedWeek.year}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {reportList.length} report{reportList.length !== 1 ? "s" : ""}{" "}
                  available
                </Typography>
              </Box>
            )
          ) : detailLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                gap: 1,
                p: 1,
                overflow: "hidden",
              }}
            >
              {isMobile ? (
                <Box sx={{ ...skeletonSx, flex: 1, height: "100%" }} />
              ) : (
                <>
                  <Box
                    sx={{
                      ...skeletonSx,
                      width: `${splitPercent}%`,
                      height: "100%",
                    }}
                  />
                  <Box
                    sx={{
                      ...skeletonSx,
                      width: `${100 - splitPercent}%`,
                      height: "100%",
                    }}
                  />
                </>
              )}
            </Box>
          ) : (
            <Box
              ref={splitContainerRef}
              sx={{ flex: 1, display: "flex", overflow: "hidden" }}
            >
              {/* Left Pane: Source PDF rendered via pdf.js — hidden on mobile */}
              {!isMobile && (
                <Box
                  sx={{
                    width: `${splitPercent}%`,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    borderRight: "1px solid #e0e0e0",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {/* PDF Toolbar */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 0.5,
                      bgcolor: "#f5f5f5",
                      borderBottom: "1px solid #e0e0e0",
                      minHeight: 40,
                      flexShrink: 0,
                    }}
                  >
                    <PictureAsPdfIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                    {selectedReport && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#1B3A6B",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 280,
                          transition: "opacity 200ms ease",
                        }}
                        title={selectedReport.showing_name}
                      >
                        {selectedReport.showing_name}
                      </Typography>
                    )}
                    {appendedReports.length > 1 && (
                      <Tooltip
                        title={`Continuous view of ${appendedReports.length} reports`}
                      >
                        <Box
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: ACCENT_ORANGE,
                            bgcolor: "rgba(255,153,0,0.12)",
                            border: "1px solid rgba(255,153,0,0.35)",
                            borderRadius: 999,
                            px: 0.75,
                            py: "1px",
                            ml: 0.5,
                            letterSpacing: 0.4,
                          }}
                        >
                          {appendedReports.length} loaded
                        </Box>
                      </Tooltip>
                    )}
                    {(pdfLoading || appendingNext || appendingPrev) && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: 10,
                          fontWeight: 700,
                          color: "text.secondary",
                          bgcolor: "rgba(0,0,0,0.04)",
                          border: "1px solid rgba(0,0,0,0.12)",
                          borderRadius: 999,
                          px: 0.75,
                          py: "1px",
                          ml: 0.5,
                          letterSpacing: 0.4,
                          textTransform: "uppercase",
                        }}
                      >
                        <CircularProgress
                          size={9}
                          thickness={6}
                          sx={{ color: ACCENT_ORANGE, flexShrink: 0 }}
                        />
                        {pdfLoading
                          ? "Loading…"
                          : appendingPrev
                          ? "Loading previous…"
                          : "Loading next…"}
                      </Box>
                    )}
                    <Box sx={{ flex: 1 }} />

                    {/* Page navigation */}
                    {totalPdfPages > 0 && (
                      <>
                        <Tooltip title="Previous page">
                          <span>
                            <IconButton
                              size="small"
                              disabled={currentPage <= 1}
                              onClick={() => goToPage(currentPage - 1)}
                              aria-label="Previous page"
                            >
                              <NavigateBeforeIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Typography
                          variant="caption"
                          sx={{
                            mx: 0.75,
                            fontVariantNumeric: "tabular-nums",
                            color: "text.secondary",
                            userSelect: "none",
                            minWidth: 48,
                            textAlign: "center",
                          }}
                          aria-label={`Page ${currentPage} of ${totalPdfPages}`}
                        >
                          {currentPage} / {totalPdfPages}
                        </Typography>
                        <Tooltip title="Next page">
                          <span>
                            <IconButton
                              size="small"
                              disabled={currentPage >= totalPdfPages}
                              onClick={() => goToPage(currentPage + 1)}
                              aria-label="Next page"
                            >
                              <NavigateNextIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", mr: 1 }}
                    >
                      {pdfZoom}%
                    </Typography>
                    <Tooltip title="Zoom out">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setPdfZoom((z) => Math.max(z - 15, 50))
                          }
                          disabled={pdfZoom <= 50}
                          aria-label="Zoom out PDF"
                        >
                          <ZoomOutIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Zoom in">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setPdfZoom((z) => Math.min(z + 15, 250))
                          }
                          disabled={pdfZoom >= 250}
                          aria-label="Zoom in PDF"
                        >
                          <ZoomInIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {(hasHighlightedPdf || currentReportHasHighlights) && (
                      <Tooltip
                        title={
                          cellRefsEnabled
                            ? "Hide cell highlights"
                            : "Show cell highlights"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleCellRefs(!cellRefsEnabled)}
                          sx={{
                            color: cellRefsEnabled
                              ? "#FF9900"
                              : "text.secondary",
                            bgcolor: cellRefsEnabled
                              ? "rgba(255,153,0,0.1)"
                              : "transparent",
                            borderRadius: 1,
                            ml: 1,
                          }}
                          aria-label={
                            cellRefsEnabled
                              ? "Hide cell highlights"
                              : "Show cell highlights"
                          }
                        >
                          <GridOnIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Fullscreen">
                      <IconButton
                        size="small"
                        onClick={() =>
                          pdfScrollRef.current?.requestFullscreen?.()
                        }
                        aria-label="Fullscreen PDF"
                      >
                        <FullscreenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* PDF Content — rendered via pdf.js canvases in imperative container */}
                  <Box
                    sx={{
                      flex: 1,
                      position: "relative",
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      ref={pdfScrollRef}
                      sx={{
                        flex: 1,
                        overflow: "auto",
                        bgcolor: "#525659",
                      }}
                    />
                    {pdfZooming && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          zIndex: 20,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1.5,
                          bgcolor: "rgba(82, 86, 89, 0.55)",
                          pointerEvents: "none",
                        }}
                      >
                        <CircularProgress
                          size={36}
                          sx={{ color: ACCENT_ORANGE }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "#fff", fontWeight: 600 }}
                        >
                          Zooming to {pdfZoom}%…
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {!activePdfUrl && !pdfLoading && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 10,
                        color: "text.secondary",
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#ccc" }}>
                        Source PDF not available
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Draggable Divider — hidden on mobile */}
              {!isMobile && (
                <Box
                  onMouseDown={handleDividerMouseDown}
                  sx={{
                    width: 6,
                    flexShrink: 0,
                    cursor: "col-resize",
                    bgcolor: "#e0e0e0",
                    "&:hover": { bgcolor: ACCENT_ORANGE },
                    transition: "background-color 0.15s",
                  }}
                />
              )}

              {/* Right Pane: Tabbed — Executive Summary / Callouts */}
              <Box
                sx={{
                  width: isMobile ? "100%" : `${100 - splitPercent}%`,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* Unified toolbar — tabs · filter · export — hidden on mobile */}
                {!isMobile && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      bgcolor: "#fff",
                      borderBottom: "1px solid #E2E8F0",
                      flexShrink: 0,
                      minHeight: 36,
                      px: 0.5,
                      overflow: "hidden",
                    }}
                  >
                    <Tabs
                      value={rightTab}
                      onChange={(_, v) => setRightTab(v)}
                      variant="scrollable"
                      scrollButtons={false}
                      sx={{
                        minHeight: 36,
                        flexShrink: 1,
                        minWidth: 0,
                        "& .MuiTab-root": {
                          minHeight: 36,
                          minWidth: 0,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: 11.5,
                          px: 1.5,
                          py: 0,
                          color: "#64748B",
                          "&.Mui-selected": { color: "#1B3A6B" },
                        },
                        "& .MuiTabs-indicator": {
                          backgroundColor: ACCENT_ORANGE,
                          height: 2,
                          borderRadius: "2px 2px 0 0",
                        },
                      }}
                    >
                      <Tab
                        icon={<SummarizeIcon sx={{ fontSize: 13 }} />}
                        iconPosition="start"
                        label="Summary"
                      />
                      <Tab
                        icon={<DescriptionIcon sx={{ fontSize: 13 }} />}
                        iconPosition="start"
                        label={
                          totalCalloutCount > 0
                            ? `Callouts (${totalCalloutCount})`
                            : "Callouts"
                        }
                      />
                    </Tabs>

                    {/* Impact filter — inline, only when Callouts tab active */}
                    {rightTab === 1 && calloutPages.length > 0 && (
                      <>
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{ mx: 0.5, my: 0.75, borderColor: "#E2E8F0" }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                            flexShrink: 0,
                            animation: "filterSlideIn 0.2s ease-out",
                            "@keyframes filterSlideIn": {
                              from: {
                                opacity: 0,
                                transform: "translateX(-6px)",
                              },
                              to: { opacity: 1, transform: "translateX(0)" },
                            },
                          }}
                        >
                          <FilterListIcon
                            sx={{ fontSize: 12, color: "#94A3B8", mr: "2px" }}
                          />
                          {[
                            { key: "all", label: "All" },
                            { key: "large", label: "Large" },
                            { key: "large+medium", label: "Large + Medium" },
                          ].map(({ key, label }) => (
                            <Box
                              key={key}
                              component="button"
                              onClick={() => applyImpactFilter(key)}
                              sx={{
                                border: "none",
                                outline: "none",
                                cursor: "pointer",
                                fontSize: 10.5,
                                fontWeight: 600,
                                fontFamily: "inherit",
                                px: 0.75,
                                py: "2px",
                                borderRadius: 0.75,
                                whiteSpace: "nowrap",
                                lineHeight: 1.4,
                                transition: "all 0.15s ease",
                                ...(impactFilter === key
                                  ? { bgcolor: "#1B3A6B", color: "#fff" }
                                  : {
                                      bgcolor: "transparent",
                                      color: "#64748B",
                                      "&:hover": {
                                        bgcolor: "#F1F5F9",
                                        color: "#1B3A6B",
                                      },
                                    }),
                              }}
                            >
                              {label}
                            </Box>
                          ))}
                        </Box>
                      </>
                    )}

                    {/* Separator + Export icon */}
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ mx: 0.5, my: 0.75, borderColor: "#E2E8F0" }}
                    />
                    <Tooltip title="Export callout HTML" arrow>
                      <span>
                        <IconButton
                          size="small"
                          onClick={handleDownloadHtml}
                          disabled={!calloutHtmlUrl}
                          sx={{
                            p: 0.5,
                            color: calloutHtmlUrl ? "#1B3A6B" : "#CBD5E1",
                            "&:hover": { bgcolor: "#F1F5F9" },
                          }}
                          aria-label="Export callout HTML"
                        >
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}

                {/* Mobile: Single-page scrollable layout with Executive Summary + Callouts */}
                {isMobile ? (
                  <Box
                    ref={calloutPaneRef}
                    sx={{
                      flex: 1,
                      overflow: "auto",
                      bgcolor: "#F5F5F5",
                    }}
                  >
                    {/* Executive Summary Section */}
                    <Box sx={{ p: "12px 12px", pb: 0 }}>
                      <SummarySection
                        styles={
                          SUMMARY_BASE_STYLES +
                          calloutStyles +
                          EXEC_SUMMARY_PANEL_OVERRIDES +
                          MOBILE_EXEC_SUMMARY_OVERRIDES
                        }
                        callouts={reportCallouts}
                        scope={
                          selectedReport
                            ? {
                                team_id: currentTeamId,
                                week: weekToParam(
                                  selectedWeek.year,
                                  selectedWeek.week
                                ),
                                report_name: selectedReport.filename,
                              }
                            : undefined
                        }
                        kpiStrip={summaryKpiStrip}
                        overview={summaryOverview}
                        keyCallouts={summaryKeyCallouts}
                        legacyContent={summaryContent}
                        summaryLoading={summaryLoading}
                        canEdit={canEditSummary(summaryTeamId)}
                        editorOpen={summaryEditorOpen}
                        onEditClick={handleOpenSummaryEditor}
                      />
                    </Box>

                    {/* Divider between Executive Summary and Callouts */}
                    {(execSummaryHtml || calloutHtmlUrl) &&
                      calloutPages.length > 0 && (
                        <Box sx={{ px: "12px", py: 1 }}>
                          <Divider sx={{ borderColor: "#D0D5DD" }} />
                        </Box>
                      )}

                    {/* Callouts filter bar — multi-select by impact level */}
                    {calloutPages.length > 0 && (
                      <Box
                        sx={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          bgcolor: "#fff",
                          px: 1.5,
                          py: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          borderBottom: "1px solid #E8ECF4",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                        }}
                      >
                        <FilterListIcon
                          sx={{ fontSize: 13, color: "#94A3B8", flexShrink: 0 }}
                        />
                        {[
                          { key: "large", label: "Large" },
                          { key: "medium", label: "Medium" },
                          { key: "small", label: "Small" },
                        ].map(({ key, label }) => {
                          const active = mobileImpactLevels.has(key);
                          return (
                            <Box
                              key={key}
                              component="button"
                              onClick={() => toggleMobileImpactLevel(key)}
                              sx={{
                                border: "none",
                                outline: "none",
                                cursor: "pointer",
                                fontSize: 10.5,
                                fontWeight: 600,
                                fontFamily: "inherit",
                                px: 0.75,
                                py: "3px",
                                borderRadius: 0.75,
                                whiteSpace: "nowrap",
                                lineHeight: 1.3,
                                transition: "all 0.15s ease",
                                ...(active
                                  ? { bgcolor: "#1B3A6B", color: "#fff" }
                                  : {
                                      bgcolor: "#F1F5F9",
                                      color: "#94A3B8",
                                      "&:hover": {
                                        bgcolor: "#E2E8F0",
                                        color: "#475569",
                                      },
                                    }),
                              }}
                            >
                              {label}
                            </Box>
                          );
                        })}

                        <Box sx={{ flex: 1 }} />

                        {/* Download icon */}
                        <IconButton
                          size="small"
                          onClick={handleDownloadHtml}
                          disabled={!calloutHtmlUrl}
                          sx={{
                            p: 0.375,
                            color: calloutHtmlUrl ? "#1B3A6B" : "#ccc",
                            "&:hover": { bgcolor: "rgba(27,58,107,0.08)" },
                          }}
                          aria-label="Download callout HTML"
                        >
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    )}

                    {/* All Callout Pages — rendered sequentially */}
                    <Box
                      sx={{
                        px: "12px",
                        pb: "calc(24px + env(safe-area-inset-bottom, 0px))",
                      }}
                    >
                      {(() => {
                        const pageCallouts = calloutsForPage(
                          currentPage,
                          selectedReport?.filename
                        );
                        if (
                          calloutPages.length === 0 &&
                          pageCallouts.length === 0
                        ) {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                py: 4,
                                color: "text.secondary",
                              }}
                            >
                              {calloutHtmlUrl || calloutGenerating ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1.5,
                                  }}
                                >
                                  <CircularProgress
                                    size={28}
                                    sx={{ color: ACCENT_ORANGE }}
                                  />
                                  {calloutGenerating && !calloutHtmlUrl && (
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      Generating callouts…
                                      <br />
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{ color: "text.secondary" }}
                                      >
                                        This can take a few minutes. It will
                                        appear here automatically.
                                      </Typography>
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2">
                                  Callout data not available
                                </Typography>
                              )}
                            </Box>
                          );
                        }
                        return (
                          <Box ref={calloutContentRef}>
                            {pageCallouts.length > 0 ? (
                              pageCallouts.map((c) => (
                                <CalloutCard
                                  key={`mobile-${c.id}`}
                                  callout={c}
                                  isStale={isStaleCallout(c, aiGenTimestamp)}
                                  canEdit={canEditCallout(c)}
                                  canDelete={canEditCallout(c)}
                                  onEdit={handleEditCallout}
                                  onDelete={(id) => setDeleteConfirmId(id)}
                                />
                              ))
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  py: 4,
                                  color: "text.secondary",
                                }}
                              >
                                <Typography variant="body2">
                                  No callouts for page {currentPage}
                                </Typography>
                              </Box>
                            )}
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleAddCallout(currentPage)}
                              sx={{ mt: 1, textTransform: "none" }}
                            >
                              Add Callout
                            </Button>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                ) : (
                  <>
                    {/* Desktop: Tab 0: Executive Summary */}
                    {rightTab === 0 && (
                      <Box
                        sx={{
                          flex: 1,
                          overflow: "auto",
                          bgcolor: "#F5F5F5",
                          p: 2,
                        }}
                      >
                        <SummarySection
                          styles={
                            SUMMARY_BASE_STYLES +
                            calloutStyles +
                            EXEC_SUMMARY_PANEL_OVERRIDES
                          }
                          callouts={reportCallouts}
                          scope={
                            selectedReport
                              ? {
                                  team_id: currentTeamId,
                                  week: weekToParam(
                                    selectedWeek.year,
                                    selectedWeek.week
                                  ),
                                  report_name: selectedReport.filename,
                                }
                              : undefined
                          }
                          kpiStrip={summaryKpiStrip}
                          overview={summaryOverview}
                          keyCallouts={summaryKeyCallouts}
                          legacyContent={summaryContent}
                          summaryLoading={summaryLoading}
                          canEdit={canEditSummary(summaryTeamId)}
                          editorOpen={summaryEditorOpen}
                          onEditClick={handleOpenSummaryEditor}
                        />
                      </Box>
                    )}

                    {/* Desktop: Tab 1: Paged Callouts */}
                    {rightTab === 1 && (
                      <>
                        {/* Callout Content — rendered HTML cards for current page */}
                        <Box
                          ref={calloutPaneRef}
                          sx={{
                            flex: 1,
                            overflow: "auto",
                            bgcolor: "#F5F5F5",
                            p: 2,
                          }}
                        >
                          {(() => {
                            const pageCallouts = calloutsForPage(
                              currentPage,
                              selectedReport?.filename
                            );
                            if (
                              calloutPages.length === 0 &&
                              pageCallouts.length === 0
                            ) {
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100%",
                                    color: "text.secondary",
                                  }}
                                >
                                  {calloutHtmlUrl || calloutGenerating ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 1.5,
                                      }}
                                    >
                                      <CircularProgress
                                        size={28}
                                        sx={{ color: ACCENT_ORANGE }}
                                      />
                                      {calloutGenerating && !calloutHtmlUrl && (
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 600 }}
                                        >
                                          Generating callouts…
                                          <br />
                                          <Typography
                                            component="span"
                                            variant="caption"
                                            sx={{ color: "text.secondary" }}
                                          >
                                            This can take a few minutes. It will
                                            appear here automatically.
                                          </Typography>
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2">
                                      Callout data not available
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }
                            return (
                              <Box ref={calloutContentRef}>
                                {pageCallouts.length > 0 ? (
                                  pageCallouts.map((c) => (
                                    <CalloutCard
                                      key={`page-${c.id}`}
                                      callout={c}
                                      isStale={isStaleCallout(
                                        c,
                                        aiGenTimestamp
                                      )}
                                      canEdit={canEditCallout(c)}
                                      canDelete={canEditCallout(c)}
                                      onEdit={handleEditCallout}
                                      onDelete={(id) => setDeleteConfirmId(id)}
                                    />
                                  ))
                                ) : (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      py: 4,
                                      color: "text.secondary",
                                    }}
                                  >
                                    <Typography variant="body2">
                                      No callouts for page {currentPage}
                                    </Typography>
                                  </Box>
                                )}
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => handleAddCallout(currentPage)}
                                  sx={{ mt: 1, textTransform: "none" }}
                                >
                                  Add Callout
                                </Button>
                              </Box>
                            );
                          })()}
                        </Box>
                      </>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Callout Editor Dialog */}
      <CalloutEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSave={handleSaveCallout}
        mode={editorMode}
        initialData={editorCallout}
        context={{
          teamId: currentTeamId,
          week: weekToParam(selectedWeek.year, selectedWeek.week),
          reportName: selectedReport?.filename ?? "",
          pageNum: editorPageNum,
          userId: userConfig?.id ?? 0,
        }}
        error={mcError}
      />

      {/* Summary Narrative Editor Dialog (edit-callout-summary) */}
      <SummaryEditor
        open={summaryEditorOpen}
        onClose={handleCancelSummaryEditor}
        onSave={handleSaveSummary}
        initialOverview={summaryOverview ?? ""}
        initialKpiStrip={summaryKpiStrip}
        initialKeyCallouts={summaryKeyCallouts}
        callouts={reportCallouts ?? []}
        loading={summarySaving}
        error={summaryEditorOpen ? summaryError : null}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Callout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this callout? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button
            onClick={() =>
              deleteConfirmId !== null && handleDeleteCallout(deleteConfirmId)
            }
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete error snackbar */}
      <Snackbar
        open={!!deleteSnackbar}
        autoHideDuration={4000}
        onClose={() => setDeleteSnackbar(null)}
        message={deleteSnackbar}
      />

      {/* Share link snackbar */}
      <Snackbar
        open={!!shareSnackbar}
        autoHideDuration={2500}
        onClose={() => setShareSnackbar(null)}
        message={shareSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* Summary narrative load/save error snackbar — surfaces errors when the
          editor is closed (e.g. open-flow load failure, Req 1.4 / 7.2 / 8.1). */}
      <Snackbar
        open={!!summaryError && !summaryEditorOpen}
        autoHideDuration={5000}
        onClose={() => setSummaryError(null)}
        message={summaryError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      {/* WBR Chatbox — floating overlay (task 8.1). Mounted as a sibling of the
          viewer body (NOT inside the PDF/callout flex container) so it is out
          of normal document flow and cannot compress, resize, or reflow the
          PDF pane or callout panel whether open or collapsed (Req 1.1, 1.4,
          8.6, 8.7). The component self-positions with `position: fixed`,
          anchored bottom-right and layered above the panes via OVERLAY_Z_INDEX
          (Req 8.1, 8.5, 8.8). */}
      <WBRChatbox
        currentUserId={userConfig?.id ?? null}
        currentTeamId={currentTeamId}
        currentUserName={currentUserName}
        viewContext={chatboxViewContext}
        viewCallouts={chatboxViewCallouts}
        client={client}
      />
    </>
  );
};

export default WBRCalloutsViewer;
